import React from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Users, FileText, X, Mail, HandHeart, Heart, ScrollText, Crown, BookUser, Brain, ListPlus, Download, HelpCircle, Landmark, GripVertical, Cake, Play, CalendarDays, ShoppingBag, Upload, Megaphone, MousePointerClick } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { FALLBACK_IMAGE, IMAGES } from "../lib/images";
import { useSEO } from "../components/site/Reveal";
import CourseEditorModal from "../components/site/CourseEditorModal";
import AdvertEditorModal from "../components/site/AdvertEditorModal";

const EMPTY = {
  title: "",
  excerpt: "",
  content: "",
  cover_image: "",
  category: "Culture",
  tags: "",
  published: true,
};

const EMPTY_PROVERB = {
  yoruba: "",
  translation: "",
  meaning: "",
  explanation: "",
  usage: "",
  theme: "Wisdom",
  image: "",
  published: true,
};

const EMPTY_TITLE = {
  town: "",
  title: "",
  region: "Yorùbáland",
  note: "",
  image: "",
  published: true,
};

const EMPTY_QUESTION = {
  question: "",
  answer: "",
  card_caption: "",
  image: "",
  theme: "Reflection",
  answer_visible: false,
  published: true,
};

const EMPTY_MUSEUM = {
  name: "",
  location: "",
  type: "Museum",
  description: "",
  website: "",
  image: "",
  lat: "",
  lng: "",
  published: true,
};

const EMPTY_GALLERY = {
  src: "",
  caption: "",
  category: "Portraits",
  type: "image",
  poster: "",
  published: true,
};

const EMPTY_LEADER = {
  name: "",
  title: "",
  group: "Leaders",
  bio: "",
  image: "",
  published: true,
};

const EMPTY_SHOUTOUT = {
  name: "",
  title: "",
  message: "",
  image: "",
  video: "",
  date: "",
  birth_month: 0,
  birth_day: 0,
  published: true,
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const EMPTY_EVENT = {
  title: "",
  description: "",
  date: "",
  time: "",
  location: "",
  image: "",
  link: "",
  category: "Concert",
  requires_registration: false,
  registration_fee: 0,
  fee_label: "Registration Fee",
  registration_note: "",
  capacity: 0,
  faqs: [],
  published: true,
};
const EVENT_CATEGORIES = ["Concert", "Exhibition", "Festival", "Workshop", "Lecture", "Community", "Event"];

const EMPTY_PRODUCT = {
  name: "", description: "", price: 0, category: "General",
  images: [], requires_shipping: true, stock: -1, variants: [], published: true, featured: false,
};
const EMPTY_ORISA = {
  name: "", domain: "", color: "", image: "", description: "", subtitle: "", intro: "", sections: [], published: true,
};
const serializeVariants = (arr) => (arr || []).map((v) => `${v.name}: ${(v.options || []).join(", ")}`).join("\n");
const parseVariants = (text) => (text || "").split("\n").map((l) => {
  const [name, opts] = l.split(":");
  if (!name || !opts) return null;
  return { name: name.trim(), options: opts.split(",").map((o) => o.trim()).filter(Boolean) };
}).filter((v) => v && v.options.length);

const LEADER_GROUP_SUGGESTIONS = ["Leaders", "Directors", "Organizing Secretaries", "Advisors", "Patrons", "Trustees"];

const BANNERS = [
  { key: "hero_image", label: "Homepage Hero", defaultImg: IMAGES.heroMain },
  { key: "who_image", label: "Homepage 'Who Are the Yorùbá?' Image", defaultImg: IMAGES.womanBeaded },
  { key: "banner_about", label: "About Page Banner", defaultImg: IMAGES.manScarification },
  { key: "banner_membership", label: "Membership Page Banner", defaultImg: IMAGES.drummers },
  { key: "banner_leadership", label: "Leadership Page Banner", defaultImg: IMAGES.headdressShield },
  { key: "banner_shop", label: "Shop (ÀsÀ Market) Banner", defaultImg: IMAGES.indigoCloth || IMAGES.drummers },
  { key: "banner_gallery", label: "Gallery Page Banner", defaultImg: IMAGES.festivalCrowd },
  { key: "banner_events", label: "Events Page Banner", defaultImg: IMAGES.drummers },
  { key: "banner_celebrate", label: "Celebrate Page Banner", defaultImg: IMAGES.drummers },
  { key: "banner_orisa", label: "Òrìṣà Encyclopedia Banner", defaultImg: IMAGES.woodCarving },
];

const GALLERY_CATEGORIES = ["Portraits", "Art & Sculpture", "Textiles", "Festivals", "Regalia", "Food & Market"];

const EMPTY_NAME = {
  name: "",
  gender: "",
  meaning: "",
  published: true,
};

const DEFAULT_OPTION_LETTERS = ["A", "B", "C", "D", "E"];

const EMPTY_QUIZ = {
  type: "mcq",
  category: "Òrìṣà",
  question: "",
  options: ["", "", "", ""],
  option_labels: ["A", "B", "C", "D"],
  correct_index: 0,
  explanation: "",
  image: "",
  published: true,
};

const VIEWER_CONFIG = {
  subscribers: {
    title: "Newsletter Subscribers",
    empty: "No one has subscribed yet.",
    columns: [
      { key: "email", label: "Email" },
      { key: "created_at", label: "Subscribed", date: true },
    ],
  },
  members: {
    title: "Members",
    empty: "No membership requests yet.",
    columns: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "tier", label: "Tier" },
      { key: "country", label: "Country" },
      { key: "message", label: "Message" },
      { key: "created_at", label: "Joined", date: true },
    ],
  },
  volunteers: {
    title: "Volunteers",
    empty: "No volunteer applications yet.",
    columns: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "area", label: "Area" },
      { key: "phone", label: "Phone" },
      { key: "availability", label: "Availability" },
      { key: "over18", label: "18+" },
      { key: "experience", label: "Experience" },
      { key: "created_at", label: "Applied", date: true },
    ],
  },
  donations: {
    title: "Donations",
    empty: "No donations yet.",
    note: "PayPal donations are real once your keys are added. Rows marked 'pledge' were submitted via the old form and are not payments.",
    columns: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "amount", label: "Amount" },
      { key: "type", label: "Type" },
      { key: "status", label: "Status" },
      { key: "method", label: "Method" },
      { key: "project", label: "Project" },
      { key: "created_at", label: "Date", date: true },
    ],
  },
};

const fmtCell = (val, col) => {
  if (col.date && val) {
    try {
      return new Date(val).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return val;
    }
  }
  if (typeof val === "boolean") return val ? "Yes" : "No";
  return val || "—";
};

const fmtDate = (val) => {
  if (!val) return "—";
  try { return new Date(val).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return val; }
};

const fmtShip = (s) => {
  if (!s || typeof s !== "object") return "";
  return [s.name, s.line1, s.line2, [s.city, s.state, s.postal_code].filter(Boolean).join(", "), s.country]
    .filter(Boolean).join(", ");
};


const downloadCsv = (filename, columns, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => esc(typeof r[c.key] === "boolean" ? (r[c.key] ? "Yes" : "No") : r[c.key])).join(","))
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function AdminDashboard() {
  useSEO("Admin Dashboard", "Manage ÀsÀNigHT Worldwide content.");
  const { user, loading, logout } = useAuth();
  const [articles, setArticles] = React.useState([]);
  const [subscribers, setSubscribers] = React.useState([]);
  const [members, setMembers] = React.useState([]);
  const [volunteers, setVolunteers] = React.useState([]);
  const [donations, setDonations] = React.useState([]);
  const [subscriptions, setSubscriptions] = React.useState([]);
  const [cancelling, setCancelling] = React.useState(null);
  const [viewer, setViewer] = React.useState(null); // null | { key }
  const [editing, setEditing] = React.useState(null); // null | 'new' | article
  const [form, setForm] = React.useState(EMPTY);
  const [saving, setSaving] = React.useState(false);

  const [proverbs, setProverbs] = React.useState([]);
  const [editingProverb, setEditingProverb] = React.useState(null); // null | 'new' | proverb
  const [proverbForm, setProverbForm] = React.useState(EMPTY_PROVERB);
  const [savingProverb, setSavingProverb] = React.useState(false);

  const [dailyQuestions, setDailyQuestions] = React.useState([]);
  const [editingQuestion, setEditingQuestion] = React.useState(null); // null | 'new' | question
  const [questionForm, setQuestionForm] = React.useState(EMPTY_QUESTION);
  const [savingQuestion, setSavingQuestion] = React.useState(false);

  const [museums, setMuseums] = React.useState([]);
  const [editingMuseum, setEditingMuseum] = React.useState(null); // null | 'new' | museum
  const [museumForm, setMuseumForm] = React.useState(EMPTY_MUSEUM);
  const [savingMuseum, setSavingMuseum] = React.useState(false);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [uploadingCover, setUploadingCover] = React.useState(false);
  const [uploadingProverb, setUploadingProverb] = React.useState(false);
  const [uploadingQuiz, setUploadingQuiz] = React.useState(false);
  const [uploadingQuestion, setUploadingQuestion] = React.useState(false);

  const [leaders, setLeaders] = React.useState([]);
  const [editingLeader, setEditingLeader] = React.useState(null); // null | 'new' | leader
  const [leaderForm, setLeaderForm] = React.useState(EMPTY_LEADER);
  const [savingLeader, setSavingLeader] = React.useState(false);
  const [uploadingLeader, setUploadingLeader] = React.useState(false);

  const [shoutouts, setShoutouts] = React.useState([]);
  const [editingShoutout, setEditingShoutout] = React.useState(null); // null | 'new' | shoutout
  const [shoutoutForm, setShoutoutForm] = React.useState(EMPTY_SHOUTOUT);
  const [savingShoutout, setSavingShoutout] = React.useState(false);
  const [uploadingShoutout, setUploadingShoutout] = React.useState(false);
  const [uploadingShoutoutVideo, setUploadingShoutoutVideo] = React.useState(false);

  const [events, setEvents] = React.useState([]);
  const [registrations, setRegistrations] = React.useState([]);
  const [tripPhotos, setTripPhotos] = React.useState([]);
  const [waitlist, setWaitlist] = React.useState([]);
  const [balanceEdits, setBalanceEdits] = React.useState({}); // reg_id -> {balance_amount, balance_due_date}
  const [editingEvent, setEditingEvent] = React.useState(null); // null | 'new' | event
  const [eventForm, setEventForm] = React.useState(EMPTY_EVENT);
  const [savingEvent, setSavingEvent] = React.useState(false);
  const [uploadingEvent, setUploadingEvent] = React.useState(false);

  const [products, setProducts] = React.useState([]);
  const [courses, setCourses] = React.useState([]);
  const [editingCourse, setEditingCourse] = React.useState(null); // null | 'new' | course
  const [adverts, setAdverts] = React.useState([]);
  const [editingAdvert, setEditingAdvert] = React.useState(null); // null | 'new' | advert
  const [orders, setOrders] = React.useState([]);
  const [shopCats, setShopCats] = React.useState([]);
  const [shopCatsBusy, setShopCatsBusy] = React.useState(false);
  const [uploadPct, setUploadPct] = React.useState(null); // null = idle, 0-100 during upload

  const [orisaList, setOrisaList] = React.useState([]);
  const [editingOrisa, setEditingOrisa] = React.useState(null); // null | 'new' | orisa
  const [orisaForm, setOrisaForm] = React.useState(EMPTY_ORISA);
  const [savingOrisa, setSavingOrisa] = React.useState(false);
  const [uploadingOrisa, setUploadingOrisa] = React.useState(false);
  const [uploadingSection, setUploadingSection] = React.useState(null);
  const [editingProduct, setEditingProduct] = React.useState(null); // null | 'new' | product
  const [productForm, setProductForm] = React.useState(EMPTY_PRODUCT);
  const [variantsText, setVariantsText] = React.useState("");
  const [savingProduct, setSavingProduct] = React.useState(false);
  const [uploadingProduct, setUploadingProduct] = React.useState(false);

  const [banners, setBanners] = React.useState({ hero_image: "", who_image: "", banner_about: "", banner_membership: "", banner_leadership: "", banner_shop: "", banner_gallery: "", banner_events: "", banner_celebrate: "", banner_orisa: "" });
  const [bannerBusy, setBannerBusy] = React.useState("");

  const [galleryItems, setGalleryItems] = React.useState([]);
  const [editingGallery, setEditingGallery] = React.useState(null); // null | 'new' | item
  const [galleryForm, setGalleryForm] = React.useState(EMPTY_GALLERY);
  const [savingGallery, setSavingGallery] = React.useState(false);
  const [uploadingGallery, setUploadingGallery] = React.useState(false);
  const [bulkCategory, setBulkCategory] = React.useState("Portraits");
  const [bulkProgress, setBulkProgress] = React.useState(null); // null | {done,total}
  const dragIndexRef = React.useRef(null);
  const dragShoutoutRef = React.useRef(null);
  const dragTripRef = React.useRef(null);

  const [royalTitles, setRoyalTitles] = React.useState([]);
  const [editingTitle, setEditingTitle] = React.useState(null); // null | 'new' | title
  const [titleForm, setTitleForm] = React.useState(EMPTY_TITLE);
  const [savingTitle, setSavingTitle] = React.useState(false);
  const [uploadingTitle, setUploadingTitle] = React.useState(false);

  const [namesList, setNamesList] = React.useState([]);
  const [namesTotal, setNamesTotal] = React.useState(0);
  const [namesQuery, setNamesQuery] = React.useState("");
  const [editingName, setEditingName] = React.useState(null); // null | 'new' | name
  const [nameForm, setNameForm] = React.useState(EMPTY_NAME);
  const [savingName, setSavingName] = React.useState(false);

  const [quiz, setQuiz] = React.useState([]);
  const [editingQuiz, setEditingQuiz] = React.useState(null); // null | 'new' | quiz
  const [quizForm, setQuizForm] = React.useState(EMPTY_QUIZ);
  const [savingQuiz, setSavingQuiz] = React.useState(false);
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [bulkText, setBulkText] = React.useState("");
  const [savingBulk, setSavingBulk] = React.useState(false);

  const loadNames = React.useCallback((q = "") => {
    const params = new URLSearchParams();
    if (q && q.trim()) params.set("q", q.trim());
    params.set("limit", 24);
    api.get(`/admin/names?${params.toString()}`).then((r) => {
      setNamesList(r.data.items);
      setNamesTotal(r.data.total);
    }).catch(() => {});
  }, []);

  const loadAll = React.useCallback(() => {
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/newsletter").then((r) => setSubscribers(r.data)).catch(() => {});
    api.get("/membership").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/volunteer").then((r) => setVolunteers(r.data)).catch(() => {});
    api.get("/donation").then((r) => setDonations(r.data)).catch(() => {});
    api.get("/admin/subscriptions").then((r) => setSubscriptions(r.data)).catch(() => {});
    api.get("/admin/proverbs").then((r) => setProverbs(r.data)).catch(() => {});    api.get("/admin/royal-titles").then((r) => setRoyalTitles(r.data)).catch(() => {});
    api.get("/admin/quiz").then((r) => setQuiz(r.data)).catch(() => {});
    api.get("/admin/daily-questions").then((r) => setDailyQuestions(r.data)).catch(() => {});
    api.get("/admin/museums").then((r) => setMuseums(r.data)).catch(() => {});
    api.get("/admin/leaders").then((r) => setLeaders(r.data)).catch(() => {});
    api.get("/admin/shoutouts").then((r) => setShoutouts(r.data)).catch(() => {});
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/admin/event-registrations").then((r) => setRegistrations(r.data)).catch(() => {});
    api.get("/admin/trip-photos").then((r) => setTripPhotos(r.data)).catch(() => {});
    api.get("/admin/event-waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/admin/adverts").then((r) => setAdverts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/shop/categories").then((r) => setShopCats(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/admin/orisa").then((r) => setOrisaList(r.data)).catch(() => {});
    api.get("/site-settings").then((r) => setBanners((b) => ({ ...b, ...(r.data || {}) }))).catch(() => {});
    api.get("/admin/gallery").then((r) => setGalleryItems(r.data)).catch(() => {});
    loadNames("");
  }, [loadNames]);

  React.useEffect(() => {
    if (user) loadAll();
  }, [user, loadAll]);

  // Deep-link: scroll to a section when the URL has a matching hash (e.g. /admin#shop-products)
  React.useEffect(() => {
    if (!user || typeof window === "undefined") return;
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 600);
    return () => clearTimeout(t);
  }, [user]);

  if (loading) return <div className="py-32 text-center text-ink-muted">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const openEdit = (a) => {
    setForm({ ...a, tags: (a.tags || []).join(", ") });
    setEditing(a);
  };
  const close = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      excerpt: form.excerpt,
      content: form.content,
      cover_image: form.cover_image,
      category: form.category,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      published: form.published,
    };
    try {
      if (editing === "new") {
        await api.post("/admin/articles", payload);
        toast.success("Article created.");
      } else {
        await api.put(`/admin/articles/${editing.id}`, payload);
        toast.success("Article updated.");
      }
      close();
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("Article deleted.");
      loadAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const openNewProverb = () => {
    setProverbForm(EMPTY_PROVERB);
    setEditingProverb("new");
  };
  const openEditProverb = (p) => {
    setProverbForm({ ...p, translation: p.translation || "", explanation: p.explanation || "", usage: p.usage || "" });
    setEditingProverb(p);
  };
  const closeProverb = () => {
    setEditingProverb(null);
    setProverbForm(EMPTY_PROVERB);
  };

  const saveProverb = async (e) => {
    e.preventDefault();
    setSavingProverb(true);
    const payload = {
      yoruba: proverbForm.yoruba,
      translation: proverbForm.translation || "",
      meaning: proverbForm.meaning,
      explanation: proverbForm.explanation || "",
      usage: proverbForm.usage || "",
      theme: proverbForm.theme,
      image: proverbForm.image || "",
      published: proverbForm.published,
    };
    try {
      if (editingProverb === "new") {
        await api.post("/admin/proverbs", payload);
        toast.success("Proverb created.");
      } else {
        await api.put(`/admin/proverbs/${editingProverb.id}`, payload);
        toast.success("Proverb updated.");
      }
      closeProverb();
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingProverb(false);
    }
  };

  const removeProverb = async (p) => {
    if (!window.confirm("Delete this proverb?")) return;
    try {
      await api.delete(`/admin/proverbs/${p.id}`);
      toast.success("Proverb deleted.");
      loadAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const openNewQuestion = () => {
    setQuestionForm(EMPTY_QUESTION);
    setEditingQuestion("new");
  };
  const openEditQuestion = (q) => {
    setQuestionForm({ ...q });
    setEditingQuestion(q);
  };
  const closeQuestion = () => {
    setEditingQuestion(null);
    setQuestionForm(EMPTY_QUESTION);
  };

  const saveQuestion = async (e) => {
    e.preventDefault();
    if (!questionForm.question.trim()) { toast.error("Question is required."); return; }
    setSavingQuestion(true);
    const payload = {
      question: questionForm.question,
      answer: questionForm.answer,
      card_caption: questionForm.card_caption,
      image: questionForm.image || "",
      theme: questionForm.theme || "Reflection",
      answer_visible: questionForm.answer_visible,
      published: questionForm.published,
    };
    try {
      if (editingQuestion === "new") {
        await api.post("/admin/daily-questions", payload);
        toast.success("Daily question created.");
      } else {
        await api.put(`/admin/daily-questions/${editingQuestion.id}`, payload);
        toast.success("Daily question updated.");
      }
      closeQuestion();
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingQuestion(false);
    }
  };

  const removeQuestion = async (q) => {
    if (!window.confirm("Delete this daily question?")) return;
    try {
      await api.delete(`/admin/daily-questions/${q.id}`);
      toast.success("Daily question deleted.");
      loadAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const openNewMuseum = () => {
    setMuseumForm(EMPTY_MUSEUM);
    setEditingMuseum("new");
  };
  const openEditMuseum = (m) => {
    setMuseumForm({ ...m, lat: m.lat ?? "", lng: m.lng ?? "" });
    setEditingMuseum(m);
  };
  const closeMuseum = () => {
    setEditingMuseum(null);
    setMuseumForm(EMPTY_MUSEUM);
  };

  const saveMuseum = async (e) => {
    e.preventDefault();
    if (!museumForm.name.trim()) { toast.error("Name is required."); return; }
    setSavingMuseum(true);
    const payload = {
      name: museumForm.name,
      location: museumForm.location,
      type: museumForm.type || "Museum",
      description: museumForm.description,
      website: museumForm.website,
      image: museumForm.image,
      lat: museumForm.lat === "" || museumForm.lat === null ? null : parseFloat(museumForm.lat),
      lng: museumForm.lng === "" || museumForm.lng === null ? null : parseFloat(museumForm.lng),
      published: museumForm.published,
    };
    try {
      if (editingMuseum === "new") {
        await api.post("/admin/museums", payload);
        toast.success("Institution created.");
      } else {
        await api.put(`/admin/museums/${editingMuseum.id}`, payload);
        toast.success("Institution updated.");
      }
      closeMuseum();
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingMuseum(false);
    }
  };

  const removeMuseum = async (m) => {
    if (!window.confirm("Delete this institution?")) return;
    try {
      await api.delete(`/admin/museums/${m.id}`);
      toast.success("Institution deleted.");
      loadAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const openNewLeader = () => {
    setLeaderForm(EMPTY_LEADER);
    setEditingLeader("new");
  };
  const openEditLeader = (l) => {
    setLeaderForm({ ...l });
    setEditingLeader(l);
  };
  const closeLeader = () => {
    setEditingLeader(null);
    setLeaderForm(EMPTY_LEADER);
  };

  const saveLeader = async (e) => {
    e.preventDefault();
    if (!leaderForm.name.trim()) { toast.error("Name is required."); return; }
    setSavingLeader(true);
    const payload = {
      name: leaderForm.name,
      title: leaderForm.title,
      group: (leaderForm.group || "Leaders").trim() || "Leaders",
      bio: leaderForm.bio,
      image: leaderForm.image || "",
      published: leaderForm.published,
    };
    try {
      if (editingLeader === "new") {
        await api.post("/admin/leaders", payload);
        toast.success("Leader added.");
      } else {
        await api.put(`/admin/leaders/${editingLeader.id}`, payload);
        toast.success("Leader updated.");
      }
      closeLeader();
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingLeader(false);
    }
  };

  const removeLeader = async (l) => {
    if (!window.confirm("Delete this person?")) return;
    try {
      await api.delete(`/admin/leaders/${l.id}`);
      toast.success("Person deleted.");
      loadAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const uploadImageFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploadPct(0);
    try {
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.min(99, Math.round((e.loaded / e.total) * 100)));
        },
      });
      return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
    } finally {
      setUploadPct(null);
    }
  };

  const uploadMuseumImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setUploadingImage(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setMuseumForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingImage(false);
    }
  };

  const uploadArticleCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingCover(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setForm((f) => ({ ...f, cover_image: fullUrl }));
      toast.success("Cover image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingCover(false);
    }
  };

  const uploadProverbImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingProverb(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setProverbForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingProverb(false);
    }
  };

  const uploadQuizImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingQuiz(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setQuizForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingQuiz(false);
    }
  };

  const uploadQuestionImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingQuestion(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setQuestionForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingQuestion(false);
    }
  };

  const uploadLeaderImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingLeader(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setLeaderForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingLeader(false);
    }
  };

  const uploadTitleImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingTitle(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setTitleForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingTitle(false);
    }
  };

  const openNewShoutout = () => { setShoutoutForm(EMPTY_SHOUTOUT); setEditingShoutout("new"); };
  const openEditShoutout = (s) => { setShoutoutForm({ ...s, birth_month: s.birth_month || 0, birth_day: s.birth_day || 0 }); setEditingShoutout(s); };
  const closeShoutout = () => { setEditingShoutout(null); setShoutoutForm(EMPTY_SHOUTOUT); };

  const saveShoutout = async (e) => {
    e.preventDefault();
    if (!shoutoutForm.name.trim()) { toast.error("Name is required."); return; }
    setSavingShoutout(true);
    const payload = {
      name: shoutoutForm.name,
      title: shoutoutForm.title,
      message: shoutoutForm.message,
      image: shoutoutForm.image || "",
      video: shoutoutForm.video || "",
      date: shoutoutForm.date,
      birth_month: Number(shoutoutForm.birth_month) || 0,
      birth_day: Number(shoutoutForm.birth_day) || 0,
      published: shoutoutForm.published,
    };
    try {
      if (editingShoutout === "new") {
        await api.post("/admin/shoutouts", payload);
        toast.success("Birthday shoutout added.");
      } else {
        await api.put(`/admin/shoutouts/${editingShoutout.id}`, payload);
        toast.success("Shoutout updated.");
      }
      closeShoutout();
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingShoutout(false);
    }
  };

  const removeShoutout = async (s) => {
    if (!window.confirm(`Delete the birthday shoutout for ${s.name}?`)) return;
    try {
      await api.delete(`/admin/shoutouts/${s.id}`);
      toast.success("Shoutout deleted.");
      loadAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const uploadShoutoutImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingShoutout(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setShoutoutForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingShoutout(false);
    }
  };

  const uploadShoutoutVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingShoutoutVideo(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setShoutoutForm((f) => ({ ...f, video: fullUrl }));
      toast.success("Video uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingShoutoutVideo(false);
    }
  };

  const openNewEvent = () => { setEventForm(EMPTY_EVENT); setEditingEvent("new"); };
  const openEditEvent = (ev) => { setEventForm({ ...ev }); setEditingEvent(ev); };
  const closeEvent = () => { setEditingEvent(null); setEventForm(EMPTY_EVENT); };

  const saveEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title.trim()) { toast.error("Title is required."); return; }
    setSavingEvent(true);
    const payload = {
      title: eventForm.title,
      description: eventForm.description,
      date: eventForm.date,
      time: eventForm.time,
      location: eventForm.location,
      image: eventForm.image || "",
      link: eventForm.link,
      category: eventForm.category || "Event",
      requires_registration: !!eventForm.requires_registration,
      registration_fee: Number(eventForm.registration_fee) || 0,
      fee_label: eventForm.fee_label || "Registration Fee",
      registration_note: eventForm.registration_note || "",
      capacity: Number(eventForm.capacity) || 0,
      faqs: (eventForm.faqs || []).filter((x) => (x.q || "").trim() && (x.a || "").trim()),
      published: eventForm.published,
    };
    try {
      if (editingEvent === "new") {
        await api.post("/admin/events", payload);
        toast.success("Event added.");
      } else {
        await api.put(`/admin/events/${editingEvent.id}`, payload);
        toast.success("Event updated.");
      }
      closeEvent();
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingEvent(false);
    }
  };

  const removeEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await api.delete(`/admin/events/${ev.id}`);
      toast.success("Event deleted.");
      loadAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const reloadRegistrations = () => { api.get("/admin/event-registrations").then((r) => setRegistrations(r.data)).catch(() => {}); };
  const removeRegistration = async (r) => {
    if (!window.confirm(`Delete registration from "${r.full_name}"?`)) return;
    try { await api.delete(`/admin/event-registrations/${r.id}`); toast.success("Registration deleted."); reloadRegistrations(); }
    catch { toast.error("Delete failed."); }
  };
  const saveBalance = async (r) => {
    const edit = balanceEdits[r.id] || {};
    try {
      await api.put(`/admin/event-registrations/${r.id}/balance`, {
        balance_amount: Number(edit.balance_amount ?? r.balance_amount) || 0,
        balance_due_date: edit.balance_due_date ?? r.balance_due_date ?? "",
      });
      toast.success("Balance saved.");
      reloadRegistrations();
    } catch { toast.error("Save failed."); }
  };
  const sendBalanceReminder = async (r) => {
    try { await api.post(`/admin/event-registrations/${r.id}/send-balance-reminder`); toast.success(`Reminder emailed to ${r.email}.`); reloadRegistrations(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send reminder."); }
  };
  const sendAllDueReminders = async () => {
    try { const { data } = await api.post(`/admin/registrations/send-due-reminders`); toast.success(`${data.sent} due reminder(s) sent.`); reloadRegistrations(); }
    catch { toast.error("Could not send reminders."); }
  };

  const reloadTripPhotos = () => { api.get("/admin/trip-photos").then((r) => setTripPhotos(r.data)).catch(() => {}); };
  const uploadTripPhoto = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    const isVideo = (file.type || "").startsWith("video/");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      await api.post("/admin/trip-photos", { image_url: url, caption: "", media_type: isVideo ? "video" : "image" });
      toast.success(isVideo ? "Video added." : "Photo added.");
      reloadTripPhotos();
    } catch { toast.error("Upload failed."); }
  };
  const removeTripPhoto = async (p) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await api.delete(`/admin/trip-photos/${p.id}`); toast.success("Photo deleted."); reloadTripPhotos(); }
    catch { toast.error("Delete failed."); }
  };
  const saveTripCaption = async (p, caption) => {
    if ((p.caption || "") === caption) return;
    try { await api.put(`/admin/trip-photos/${p.id}`, { image_url: p.image_url, caption, media_type: p.media_type || "image", poster: p.poster || "", published: p.published !== false }); }
    catch { toast.error("Couldn't save caption."); }
  };
  const uploadTripPoster = async (p, e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      await api.put(`/admin/trip-photos/${p.id}`, { image_url: p.image_url, caption: p.caption || "", media_type: p.media_type || "video", poster: url, published: p.published !== false });
      toast.success("Cover image set.");
      reloadTripPhotos();
    } catch { toast.error("Upload failed."); }
  };
  const removeTripPoster = async (p) => {
    try {
      await api.put(`/admin/trip-photos/${p.id}`, { image_url: p.image_url, caption: p.caption || "", media_type: p.media_type || "video", poster: "", published: p.published !== false });
      reloadTripPhotos();
    } catch { toast.error("Couldn't remove cover."); }
  };
  const handleTripDrop = async (toIndex) => {
    const from = dragTripRef.current;
    dragTripRef.current = null;
    if (from === null || from === toIndex) return;
    const next = [...tripPhotos];
    const [moved] = next.splice(from, 1);
    next.splice(toIndex, 0, moved);
    setTripPhotos(next);
    try { await api.put("/admin/trip-photos-reorder", { ids: next.map((p) => p.id) }); }
    catch { toast.error("Couldn't save the new order."); reloadTripPhotos(); }
  };

  const reloadWaitlist = () => { api.get("/admin/event-waitlist").then((r) => setWaitlist(r.data)).catch(() => {}); };
  const notifyWaitlist = async (w) => {
    try { await api.post(`/admin/event-waitlist/${w.id}/notify`); toast.success(`Notified ${w.email}.`); reloadWaitlist(); }
    catch { toast.error("Could not notify."); }
  };
  const removeWaitlist = async (w) => {
    if (!window.confirm(`Remove ${w.full_name} from the waitlist?`)) return;
    try { await api.delete(`/admin/event-waitlist/${w.id}`); toast.success("Removed."); reloadWaitlist(); }
    catch { toast.error("Delete failed."); }
  };

  const uploadEventImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingEvent(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setEventForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingEvent(false);
    }
  };

  const openNewProduct = () => { setProductForm(EMPTY_PRODUCT); setVariantsText(""); setEditingProduct("new"); };
  const openEditProduct = (p) => { setProductForm({ ...p, images: p.images || [], variants: p.variants || [] }); setVariantsText(serializeVariants(p.variants)); setEditingProduct(p); };
  const closeProduct = () => { setEditingProduct(null); setProductForm(EMPTY_PRODUCT); setVariantsText(""); };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!productForm.name.trim()) { toast.error("Name is required."); return; }
    setSavingProduct(true);
    const payload = {
      name: productForm.name, description: productForm.description,
      price: Number(productForm.price) || 0, category: productForm.category || "General",
      images: productForm.images || [], requires_shipping: productForm.requires_shipping,
      stock: Number(productForm.stock) ?? -1, variants: parseVariants(variantsText),
      published: productForm.published, featured: !!productForm.featured,
    };
    try {
      if (editingProduct === "new") { await api.post("/admin/products", payload); toast.success("Product added."); }
      else { await api.put(`/admin/products/${editingProduct.id}`, payload); toast.success("Product updated."); }
      closeProduct(); loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally { setSavingProduct(false); }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Product deleted."); loadAll(); }
    catch { toast.error("Delete failed."); }
  };

  const reloadCourses = () => { api.get("/admin/courses").then((r) => setCourses(r.data)).catch(() => {}); };
  const removeCourse = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    try { await api.delete(`/admin/courses/${c.id}`); toast.success("Course deleted."); reloadCourses(); }
    catch { toast.error("Delete failed."); }
  };

  const reloadAdverts = () => { api.get("/admin/adverts").then((r) => setAdverts(r.data)).catch(() => {}); };
  const removeAdvert = async (a) => {
    if (!window.confirm("Delete this advert?")) return;
    try { await api.delete(`/admin/adverts/${a.id}`); toast.success("Advert deleted."); reloadAdverts(); }
    catch { toast.error("Delete failed."); }
  };

  const addShopCat = () => setShopCats((c) => [...c, ""]);
  const renameShopCat = (i, val) => setShopCats((c) => c.map((x, idx) => (idx === i ? val : x)));
  const removeShopCat = (i) => setShopCats((c) => c.filter((_, idx) => idx !== i));
  const moveShopCat = (i, dir) => setShopCats((c) => {
    const arr = [...c]; const j = i + dir;
    if (j < 0 || j >= arr.length) return c;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });
  const saveShopCats = async () => {
    setShopCatsBusy(true);
    try {
      const { data } = await api.put("/admin/shop/categories", { categories: shopCats });
      setShopCats(Array.isArray(data) ? data : []);
      toast.success("Categories saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally { setShopCatsBusy(false); }
  };

  const openNewOrisa = () => { setOrisaForm(EMPTY_ORISA); setEditingOrisa("new"); };
  const openEditOrisa = (o) => { setOrisaForm({ ...EMPTY_ORISA, ...o, sections: (o.sections || []).map((s) => ({ heading: s.heading || "", body: s.body || "", image: s.image || "" })) }); setEditingOrisa(o); };
  const closeOrisa = () => { setEditingOrisa(null); setOrisaForm(EMPTY_ORISA); };
  const updateSection = (i, key, val) => setOrisaForm((f) => ({ ...f, sections: f.sections.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)) }));
  const addSection = () => setOrisaForm((f) => ({ ...f, sections: [...f.sections, { heading: "", body: "", image: "" }] }));
  const removeSection = (i) => setOrisaForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));
  const moveSection = (i, dir) => setOrisaForm((f) => {
    const arr = [...f.sections]; const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, sections: arr };
  });

  const saveOrisa = async (e) => {
    e.preventDefault();
    if (!orisaForm.name.trim()) { toast.error("Name is required."); return; }
    setSavingOrisa(true);
    const payload = {
      name: orisaForm.name, domain: orisaForm.domain, color: orisaForm.color,
      image: orisaForm.image, description: orisaForm.description, subtitle: orisaForm.subtitle,
      intro: orisaForm.intro,
      sections: (orisaForm.sections || []).filter((s) => (s.heading || "").trim() || (s.body || "").trim() || (s.image || "").trim()),
      published: orisaForm.published,
    };
    try {
      if (editingOrisa === "new") { await api.post("/admin/orisa", payload); toast.success("Òrìṣà added."); }
      else { await api.put(`/admin/orisa/${editingOrisa.id}`, payload); toast.success("Òrìṣà updated."); }
      closeOrisa(); loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally { setSavingOrisa(false); }
  };

  const removeOrisa = async (o) => {
    if (!window.confirm(`Delete "${o.name}"?`)) return;
    try { await api.delete(`/admin/orisa/${o.id}`); toast.success("Òrìṣà deleted."); loadAll(); }
    catch { toast.error("Delete failed."); }
  };

  const uploadOrisaImage = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUploadingOrisa(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setOrisaForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploadingOrisa(false); }
  };

  const uploadSectionImage = async (i, e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUploadingSection(i);
    try {
      const fullUrl = await uploadImageFile(file);
      updateSection(i, "image", fullUrl);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploadingSection(null); }
  };

  const uploadProductImage = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingProduct(true);
    try {
      for (const file of files) {
        const fullUrl = await uploadImageFile(file);
        setProductForm((f) => ({ ...f, images: [...(f.images || []), fullUrl] }));
      }
      toast.success("Image(s) uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploadingProduct(false); }
  };

  const uploadBanner = async (key, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBannerBusy(`upload-${key}`);
    try {
      const fullUrl = await uploadImageFile(file);
      setBanners((b) => ({ ...b, [key]: fullUrl }));
      await api.put("/admin/site-settings", { [key]: fullUrl });
      toast.success("Banner updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setBannerBusy("");
    }
  };

  const saveBanner = async (key) => {
    setBannerBusy(`save-${key}`);
    try {
      await api.put("/admin/site-settings", { [key]: banners[key] || "" });
      toast.success(banners[key] ? "Banner saved." : "Reset to default.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setBannerBusy("");
    }
  };

  const openNewGallery = () => {
    setGalleryForm(EMPTY_GALLERY);
    setEditingGallery("new");
  };
  const openEditGallery = (g) => {
    setGalleryForm({ ...g, type: g.type || "image", poster: g.poster || "" });
    setEditingGallery(g);
  };
  const closeGallery = () => {
    setEditingGallery(null);
    setGalleryForm(EMPTY_GALLERY);
  };

  const uploadGalleryImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isVideo = (file.type || "").startsWith("video/");
    setUploadingGallery(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setGalleryForm((f) => ({ ...f, src: fullUrl, type: isVideo ? "video" : "image" }));
      toast.success(isVideo ? "Video uploaded." : "Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingGallery(false);
    }
  };

  const uploadGalleryPoster = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingGallery(true);
    try {
      const fullUrl = await uploadImageFile(file);
      setGalleryForm((f) => ({ ...f, poster: fullUrl }));
      toast.success("Cover image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploadingGallery(false);
    }
  };

  const saveGallery = async (e) => {
    e.preventDefault();
    if (!galleryForm.src.trim()) { toast.error("Please upload or paste an image first."); return; }
    setSavingGallery(true);
    const payload = {
      src: galleryForm.src,
      caption: galleryForm.caption,
      category: galleryForm.category || "Portraits",
      type: galleryForm.type || "image",
      poster: galleryForm.poster || "",
      published: galleryForm.published,
    };
    try {
      if (editingGallery === "new") {
        await api.post("/admin/gallery", payload);
        toast.success("Photo added.");
      } else {
        await api.put(`/admin/gallery/${editingGallery.id}`, payload);
        toast.success("Photo updated.");
      }
      closeGallery();
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingGallery(false);
    }
  };

  const removeGallery = async (g) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/admin/gallery/${g.id}`);
      toast.success("Photo removed.");
      loadAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const handleGalleryDrop = async (toIndex) => {
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from === null || from === toIndex) return;
    const next = [...galleryItems];
    const [moved] = next.splice(from, 1);
    next.splice(toIndex, 0, moved);
    setGalleryItems(next);
    try {
      await api.put("/admin/gallery-reorder", { ids: next.map((g) => g.id) });
    } catch {
      toast.error("Couldn't save the new order.");
      loadAll();
    }
  };

  const handleShoutoutDrop = async (toIndex) => {
    const from = dragShoutoutRef.current;
    dragShoutoutRef.current = null;
    if (from === null || from === toIndex) return;
    const next = [...shoutouts];
    const [moved] = next.splice(from, 1);
    next.splice(toIndex, 0, moved);
    setShoutouts(next);
    try {
      await api.put("/admin/shoutouts-reorder", { ids: next.map((s) => s.id) });
    } catch {
      toast.error("Couldn't save the new order.");
      loadAll();
    }
  };

  const bulkUploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBulkProgress({ done: 0, total: files.length });
    const urls = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const url = await uploadImageFile(files[i]);
        urls.push(url);
      } catch {
        toast.error(`Couldn't upload ${files[i].name}`);
      }
      setBulkProgress({ done: i + 1, total: files.length });
    }
    if (urls.length) {
      try {
        const items = urls.map((src) => ({ src, category: bulkCategory }));
        const { data } = await api.post("/admin/gallery/bulk", { items });
        toast.success(`${data.created} photo${data.created === 1 ? "" : "s"} added to ${bulkCategory}.`);
        loadAll();
      } catch {
        toast.error("Couldn't save the uploaded photos.");
      }
    }
    setBulkProgress(null);
  };

  const openNewTitle = () => {
    setTitleForm(EMPTY_TITLE);
    setEditingTitle("new");
  };
  const openEditTitle = (t) => {
    setTitleForm({ ...t, image: t.image || "" });
    setEditingTitle(t);
  };
  const closeTitle = () => {
    setEditingTitle(null);
    setTitleForm(EMPTY_TITLE);
  };

  const saveTitle = async (e) => {
    e.preventDefault();
    setSavingTitle(true);
    const payload = {
      town: titleForm.town,
      title: titleForm.title,
      region: titleForm.region,
      note: titleForm.note,
      image: titleForm.image || "",
      published: titleForm.published,
    };
    try {
      if (editingTitle === "new") {
        await api.post("/admin/royal-titles", payload);
        toast.success("Royal title created.");
      } else {
        await api.put(`/admin/royal-titles/${editingTitle.id}`, payload);
        toast.success("Royal title updated.");
      }
      closeTitle();
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingTitle(false);
    }
  };

  const removeTitle = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await api.delete(`/admin/royal-titles/${t.id}`);
      toast.success("Royal title deleted.");
      loadAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const openNewName = () => { setNameForm(EMPTY_NAME); setEditingName("new"); };
  const openEditName = (n) => { setNameForm({ ...n }); setEditingName(n); };
  const closeName = () => { setEditingName(null); setNameForm(EMPTY_NAME); };

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    const payload = {
      name: nameForm.name,
      gender: nameForm.gender,
      meaning: nameForm.meaning,
      published: nameForm.published,
    };
    try {
      if (editingName === "new") {
        await api.post("/admin/names", payload);
        toast.success("Name created.");
      } else {
        await api.put(`/admin/names/${editingName.id}`, payload);
        toast.success("Name updated.");
      }
      closeName();
      loadNames(namesQuery);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingName(false);
    }
  };

  const removeName = async (n) => {
    if (!window.confirm(`Delete "${n.name}"?`)) return;
    try {
      await api.delete(`/admin/names/${n.id}`);
      toast.success("Name deleted.");
      loadNames(namesQuery);
    } catch {
      toast.error("Delete failed.");
    }
  };

  const openNewQuiz = () => { setQuizForm(EMPTY_QUIZ); setEditingQuiz("new"); };
  const openEditQuiz = (q) => {
    const opts = q.type === "boolean" ? ["True", "False"] : [...(q.options || []), "", "", "", ""].slice(0, 4);
    const labels = [...(q.option_labels || []), "A", "B", "C", "D"].slice(0, opts.length).map((l, i) => l || DEFAULT_OPTION_LETTERS[i]);
    setQuizForm({
      type: q.type || "mcq",
      category: q.category || "",
      question: q.question || "",
      options: opts,
      option_labels: labels,
      correct_index: q.correct_index || 0,
      explanation: q.explanation || "",
      image: q.image || "",
      published: q.published !== false,
    });
    setEditingQuiz(q);
  };
  const closeQuiz = () => { setEditingQuiz(null); setQuizForm(EMPTY_QUIZ); };

  const setQuizType = (type) => {
    if (type === "boolean") {
      setQuizForm((f) => ({ ...f, type, options: ["True", "False"], correct_index: f.correct_index > 1 ? 0 : f.correct_index }));
    } else {
      setQuizForm((f) => ({
        ...f, type,
        options: [...(f.options || []), "", "", "", ""].slice(0, 4),
        option_labels: [...(f.option_labels || []), "A", "B", "C", "D"].slice(0, 4).map((l, i) => l || DEFAULT_OPTION_LETTERS[i]),
        correct_index: 0,
      }));
    }
  };

  const saveQuiz = async (e) => {
    e.preventDefault();
    let options, option_labels, correct_index;
    if (quizForm.type === "boolean") {
      options = ["True", "False"];
      option_labels = [];
      correct_index = quizForm.correct_index === 1 ? 1 : 0;
    } else {
      const kept = (quizForm.options || [])
        .map((o, i) => ({ text: (o || "").trim(), label: ((quizForm.option_labels || [])[i] || DEFAULT_OPTION_LETTERS[i] || "").trim(), origIdx: i }))
        .filter((x) => x.text);
      if (kept.length < 2) { toast.error("Add at least two answer options."); return; }
      options = kept.map((k) => k.text);
      option_labels = kept.map((k, idx) => k.label || DEFAULT_OPTION_LETTERS[idx]);
      correct_index = kept.findIndex((k) => k.origIdx === quizForm.correct_index);
      if (correct_index < 0) { toast.error("Please mark the correct answer."); return; }
    }
    if (!quizForm.question.trim()) { toast.error("Question is required."); return; }
    setSavingQuiz(true);
    const payload = {
      question: quizForm.question,
      type: quizForm.type,
      category: quizForm.category || "General",
      options,
      option_labels,
      correct_index,
      explanation: quizForm.explanation,
      image: quizForm.image || "",
      published: quizForm.published,
    };
    try {
      if (editingQuiz === "new") {
        await api.post("/admin/quiz", payload);
        toast.success("Question created.");
      } else {
        await api.put(`/admin/quiz/${editingQuiz.id}`, payload);
        toast.success("Question updated.");
      }
      closeQuiz();
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingQuiz(false);
    }
  };

  const removeQuiz = async (q) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await api.delete(`/admin/quiz/${q.id}`);
      toast.success("Question deleted.");
      loadAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const cancelSubscription = async (sub) => {
    if (!window.confirm(`Cancel the monthly donation from ${sub.name || sub.email}? This stops future PayPal charges.`)) return;
    setCancelling(sub.paypal_subscription_id);
    try {
      await api.post(`/admin/subscriptions/${sub.paypal_subscription_id}/cancel`);
      toast.success("Subscription cancelled.");
      setSubscriptions((list) => list.map((s) => (s.paypal_subscription_id === sub.paypal_subscription_id ? { ...s, status: "cancelled" } : s)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not cancel subscription.");
    } finally {
      setCancelling(null);
    }
  };

  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const saveBulk = async () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    const items = [];
    for (const line of lines) {
      const parts = line.split("|").map((p) => p.trim());
      if (parts.length < 4) continue; // need category | question | correct | at least one wrong
      const [category, question, correct, ...wrongs] = parts;
      const cleanWrongs = wrongs.filter(Boolean);
      const isBool =
        cleanWrongs.length === 1 &&
        ["true", "false"].includes(correct.toLowerCase()) &&
        ["true", "false"].includes(cleanWrongs[0].toLowerCase());
      if (isBool) {
        items.push({
          question,
          type: "boolean",
          category: category || "General",
          options: ["True", "False"],
          correct_index: correct.toLowerCase() === "true" ? 0 : 1,
          explanation: "",
          published: true,
        });
      } else {
        const opts = shuffle([correct, ...cleanWrongs]);
        items.push({
          question,
          type: "mcq",
          category: category || "General",
          options: opts,
          correct_index: opts.indexOf(correct),
          explanation: "",
          published: true,
        });
      }
    }
    if (items.length === 0) {
      toast.error("No valid lines found. Use: Category | Question | Correct | Wrong | Wrong");
      return;
    }
    setSavingBulk(true);
    try {
      const r = await api.post("/admin/quiz/bulk", items);
      toast.success(`Added ${r.data.created} question${r.data.created === 1 ? "" : "s"}.`);
      setBulkText("");
      setBulkOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk add failed.");
    } finally {
      setSavingBulk(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand">
      <div className="bg-night text-cream">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-gold-soft">Admin</p>
            <h1 className="font-serif text-2xl">ÀsÀNigHT Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-cream/70 hover:text-gold">View Site</Link>
            <button onClick={logout} data-testid="logout-button" className="inline-flex items-center gap-2 text-sm border border-cream/20 rounded-full px-4 py-2 hover:border-gold hover:text-gold transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-9 gap-4 mb-10">
          <div className="bg-cream border border-parchment rounded-xl p-6 flex items-center gap-4">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-forest/10 text-forest"><FileText className="w-5 h-5" /></span>
            <div>
              <p className="text-3xl font-serif text-ink" data-testid="stat-articles">{articles.length}</p>
              <p className="text-sm text-ink-muted">Articles</p>
            </div>
          </div>
          <button onClick={() => setViewer("subscribers")} data-testid="card-subscribers" className="text-left bg-cream border border-parchment rounded-xl p-6 flex items-center gap-4 hover:border-gold hover:shadow-sm transition-all">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold"><Mail className="w-5 h-5" /></span>
            <div>
              <p className="text-3xl font-serif text-ink" data-testid="stat-subscribers">{subscribers.length}</p>
              <p className="text-sm text-ink-muted">Subscribers</p>
            </div>
          </button>
          <button onClick={() => setViewer("members")} data-testid="card-members" className="text-left bg-cream border border-parchment rounded-xl p-6 flex items-center gap-4 hover:border-forest hover:shadow-sm transition-all">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-forest/10 text-forest"><Users className="w-5 h-5" /></span>
            <div>
              <p className="text-3xl font-serif text-ink" data-testid="stat-members">{members.length}</p>
              <p className="text-sm text-ink-muted">Members</p>
            </div>
          </button>
          <button onClick={() => setViewer("volunteers")} data-testid="card-volunteers" className="text-left bg-cream border border-parchment rounded-xl p-6 flex items-center gap-4 hover:border-gold hover:shadow-sm transition-all">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold"><HandHeart className="w-5 h-5" /></span>
            <div>
              <p className="text-3xl font-serif text-ink" data-testid="stat-volunteers">{volunteers.length}</p>
              <p className="text-sm text-ink-muted">Volunteers</p>
            </div>
          </button>
          <button onClick={() => setViewer("donations")} data-testid="card-donations" className="text-left bg-cream border border-parchment rounded-xl p-6 flex items-center gap-4 hover:border-forest hover:shadow-sm transition-all">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-forest/10 text-forest"><Heart className="w-5 h-5" /></span>
            <div>
              <p className="text-3xl font-serif text-ink" data-testid="stat-donations">{donations.length}</p>
              <p className="text-sm text-ink-muted">Donations</p>
            </div>
          </button>
          <div className="bg-cream border border-parchment rounded-xl p-6 flex items-center gap-4">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold"><ScrollText className="w-5 h-5" /></span>
            <div>
              <p className="text-3xl font-serif text-ink" data-testid="stat-proverbs">{proverbs.length}</p>
              <p className="text-sm text-ink-muted">Proverbs</p>
            </div>
          </div>
          <div className="bg-cream border border-parchment rounded-xl p-6 flex items-center gap-4">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-forest/10 text-forest"><Crown className="w-5 h-5" /></span>
            <div>
              <p className="text-3xl font-serif text-ink" data-testid="stat-royal-titles">{royalTitles.length}</p>
              <p className="text-sm text-ink-muted">Royal Titles</p>
            </div>
          </div>
          <div className="bg-cream border border-parchment rounded-xl p-6 flex items-center gap-4">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold"><BookUser className="w-5 h-5" /></span>
            <div>
              <p className="text-3xl font-serif text-ink" data-testid="stat-names">{namesTotal.toLocaleString()}</p>
              <p className="text-sm text-ink-muted">Yorùbá Names</p>
            </div>
          </div>
          <div className="bg-cream border border-parchment rounded-xl p-6 flex items-center gap-4">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-forest/10 text-forest"><Brain className="w-5 h-5" /></span>
            <div>
              <p className="text-3xl font-serif text-ink" data-testid="stat-quiz">{quiz.length}</p>
              <p className="text-sm text-ink-muted">Quiz Questions</p>
            </div>
          </div>
        </div>

        {/* PAGE BANNERS MANAGER */}
        <div className="mb-16">
          <h2 className="font-serif text-3xl text-ink mb-2">Page Banners</h2>
          <p className="text-sm text-ink-muted mb-5">Swap the large banner image at the top of your key pages. Upload a new one or paste an image URL. Clear the URL and Save to restore the default.</p>
          <div className="grid gap-5 md:grid-cols-2">
            {BANNERS.map((b) => (
              <div key={b.key} data-testid={`banner-card-${b.key}`} className="bg-cream border border-parchment rounded-xl p-5">
                <p className="font-serif text-lg text-ink mb-3">{b.label}</p>
                <div className="aspect-[16/7] w-full rounded-lg overflow-hidden bg-forest-deep border border-parchment mb-3">
                  <img src={banners[b.key] || b.defaultImg} alt={`${b.label} preview`} data-testid={`banner-preview-${b.key}`} className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <label data-testid={`banner-upload-label-${b.key}`} className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                    {bannerBusy === `upload-${b.key}` ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => uploadBanner(b.key, e)} disabled={bannerBusy !== ""} data-testid={`banner-upload-input-${b.key}`} className="hidden" />
                  </label>
                  {banners[b.key] && (
                    <button onClick={() => setBanners((s) => ({ ...s, [b.key]: "" }))} data-testid={`banner-clear-${b.key}`} className="text-sm text-ink-muted hover:text-destructive transition-colors">Reset to default</button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <input value={banners[b.key]} onChange={(e) => setBanners((s) => ({ ...s, [b.key]: e.target.value }))} placeholder="https://…" data-testid={`banner-input-${b.key}`} className={`${inputCls} flex-1 min-w-[160px]`} />
                  <button onClick={() => saveBanner(b.key)} disabled={bannerBusy !== ""} data-testid={`banner-save-${b.key}`} className="bg-gold text-night font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">
                    {bannerBusy === `save-${b.key}` ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-3xl text-ink">Journal Articles</h2>
          <button onClick={openNew} data-testid="new-article-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> New Article
          </button>
        </div>

        <div className="space-y-3">
          {articles.map((a) => (
            <div key={a.id} data-testid={`admin-article-${a.slug}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-center gap-4">
              <img src={a.cover_image || FALLBACK_IMAGE} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-widest text-gold">{a.category}</span>
                  {!a.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Draft</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{a.title}</h3>
                <p className="text-sm text-ink-muted truncate">{a.excerpt}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEdit(a)} data-testid={`edit-article-${a.slug}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(a)} data-testid={`delete-article-${a.slug}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mb-5 mt-16">
          <h2 className="font-serif text-3xl text-ink">Yorùbá Proverbs (Òwe)</h2>
          <button onClick={openNewProverb} data-testid="new-proverb-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> New Proverb
          </button>
        </div>

        <div className="space-y-3">
          {proverbs.map((p) => (
            <div key={p.id} data-testid={`admin-proverb-${p.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold shrink-0"><ScrollText className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest text-gold">{p.theme}</span>
                  {!p.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{p.yoruba}</h3>
                <p className="text-sm text-ink-muted truncate">{p.meaning}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditProverb(p)} data-testid={`edit-proverb-${p.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => removeProverb(p)} data-testid={`delete-proverb-${p.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* DAILY QUESTIONS MANAGER */}
        <div className="flex items-center justify-between mb-5 mt-16">
          <h2 className="font-serif text-3xl text-ink">Question of the Day (Ìbéèrè t'òní)</h2>
          <button onClick={openNewQuestion} data-testid="new-daily-question-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> New Question
          </button>
        </div>

        <div className="space-y-3">
          {dailyQuestions.map((q) => (
            <div key={q.id} data-testid={`admin-daily-question-${q.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold shrink-0"><HelpCircle className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest text-gold">{q.theme}</span>
                  {!q.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{q.question}</h3>
                {q.answer && <p className="text-sm text-ink-muted truncate">{q.answer}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditQuestion(q)} data-testid={`edit-daily-question-${q.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => removeQuestion(q)} data-testid={`delete-daily-question-${q.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mb-5 mt-16">
          <h2 className="font-serif text-3xl text-ink">Yorùbá Royal Titles</h2>
          <button onClick={openNewTitle} data-testid="new-royal-title-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> New Royal Title
          </button>
        </div>

        <div className="space-y-3">
          {royalTitles.map((t) => (
            <div key={t.id} data-testid={`admin-royal-title-${t.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-forest/10 text-forest shrink-0"><Crown className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest text-gold">{t.region}</span>
                  {!t.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{t.title}</h3>
                <p className="text-sm text-ink-muted truncate">{t.town}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditTitle(t)} data-testid={`edit-royal-title-${t.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => removeTitle(t)} data-testid={`delete-royal-title-${t.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* MUSEUMS & INSTITUTIONS MANAGER */}
        <div className="flex items-center justify-between mb-5 mt-16">
          <h2 className="font-serif text-3xl text-ink">Museums & Cultural Institutions</h2>
          <button onClick={openNewMuseum} data-testid="new-museum-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> New Institution
          </button>
        </div>

        <div className="space-y-3">
          {museums.map((m) => (
            <div key={m.id} data-testid={`admin-museum-${m.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold shrink-0 overflow-hidden">
                {m.image ? <img src={m.image} alt="" className="w-full h-full object-cover" /> : <Landmark className="w-5 h-5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest text-gold">{m.type}</span>
                  {!m.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{m.name}</h3>
                {m.location && <p className="text-sm text-ink-muted truncate">{m.location}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditMuseum(m)} data-testid={`edit-museum-${m.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => removeMuseum(m)} data-testid={`delete-museum-${m.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* VISIONARY LEADERSHIP MANAGER */}
        <div className="flex items-center justify-between mb-2 mt-16">
          <h2 className="font-serif text-3xl text-ink">Visionary Leadership</h2>
          <button onClick={openNewLeader} data-testid="new-leader-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> Add Person
          </button>
        </div>
        <p className="text-sm text-ink-muted mb-5">Add your leaders, directors, secretaries and any custom groups. People are grouped on the public page by the "Group" field.</p>

        <div className="space-y-3">
          {leaders.length === 0 && (
            <p className="text-sm text-ink-muted" data-testid="admin-leaders-empty">No leaders added yet. Click "Add Person" to feature your first leader.</p>
          )}
          {leaders.map((l) => (
            <div key={l.id} data-testid={`admin-leader-${l.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold shrink-0 overflow-hidden">
                {l.image ? <img src={l.image} alt="" className="w-full h-full object-cover" /> : <Users className="w-5 h-5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest text-gold">{l.group}</span>
                  {!l.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{l.name}</h3>
                {l.title && <p className="text-sm text-ink-muted truncate">{l.title}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditLeader(l)} data-testid={`edit-leader-${l.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => removeLeader(l)} data-testid={`delete-leader-${l.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* BIRTHDAY SHOUTOUTS MANAGER */}
        <div className="flex items-center justify-between mb-2 mt-16">
          <h2 className="font-serif text-3xl text-ink">Birthday Shoutouts</h2>
          <button onClick={openNewShoutout} data-testid="new-shoutout-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> Add Shoutout
          </button>
        </div>
        <p className="text-sm text-ink-muted mb-5">Celebrate members and loved ones. Drag any shoutout to reorder how they appear on the public <Link to="/celebrate" className="text-forest underline">Celebrate</Link> page.</p>

        <div className="space-y-3">
          {shoutouts.length === 0 && (
            <p className="text-sm text-ink-muted" data-testid="admin-shoutouts-empty">No birthday shoutouts yet. Click "Add Shoutout" to celebrate someone.</p>
          )}
          {shoutouts.map((s, idx) => (
            <div
              key={s.id}
              data-testid={`admin-shoutout-${s.id}`}
              draggable
              onDragStart={() => { dragShoutoutRef.current = idx; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleShoutoutDrop(idx); }}
              className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4 cursor-move"
            >
              <span className="grid place-items-center text-ink-muted shrink-0 mt-1" title="Drag to reorder"><GripVertical className="w-4 h-4" /></span>
              <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold shrink-0 overflow-hidden">
                {s.image ? <img src={s.image} alt="" className="w-full h-full object-cover" /> : <Cake className="w-5 h-5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {s.date && <span className="text-xs uppercase tracking-widest text-gold">{s.date}</span>}
                  {!s.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{s.name}</h3>
                {s.title && <p className="text-sm text-ink-muted truncate">{s.title}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditShoutout(s)} data-testid={`edit-shoutout-${s.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => removeShoutout(s)} data-testid={`delete-shoutout-${s.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ÒRÌṢÀ ENCYCLOPEDIA MANAGER */}
        <div className="flex items-center justify-between mb-2 mt-16">
          <h2 className="font-serif text-3xl text-ink">Òrìṣà Encyclopedia</h2>
          <button onClick={openNewOrisa} data-testid="new-orisa-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> Add Òrìṣà
          </button>
        </div>
        <p className="text-sm text-ink-muted mb-5">Edit the deities in your <Link to="/orisa" className="text-forest underline">Òrìṣà Encyclopedia</Link> or add new ones. Each Òrìṣà has its own detail page built from the sections you write below.</p>
        <div className="space-y-3">
          {orisaList.length === 0 && <p className="text-sm text-ink-muted" data-testid="admin-orisa-empty">No Òrìṣà yet. Click "Add Òrìṣà" to create one.</p>}
          {orisaList.map((o) => (
            <div key={o.id} data-testid={`admin-orisa-${o.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-lg bg-gold/15 text-gold shrink-0 overflow-hidden">
                {o.image ? <img src={o.image} alt="" className="w-full h-full object-cover" /> : <BookUser className="w-5 h-5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest text-gold">{o.domain}</span>
                  {(o.sections || []).length > 0 && <span className="text-xs bg-forest/10 text-forest rounded px-2 py-0.5">{o.sections.length} sections</span>}
                  {!o.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{o.name}</h3>
                <p className="text-sm text-ink-muted truncate">/orisa/{o.slug}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditOrisa(o)} data-testid={`edit-orisa-${o.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => removeOrisa(o)} data-testid={`delete-orisa-${o.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>

        {/* SHOP CATEGORIES */}
        <div className="mb-2 mt-16">
          <h2 className="font-serif text-3xl text-ink">Shop Categories</h2>
          <p className="text-sm text-ink-muted mt-1">Define the browse tabs shoppers see on the <Link to="/shop" className="text-forest underline">Shop</Link> (e.g. Attire, Crafts, Art). Assign a product to one by typing the same name in its Category field.</p>
        </div>
        <div className="bg-cream border border-parchment rounded-xl p-5 max-w-2xl" data-testid="shop-categories-editor">
          <div className="space-y-2">
            {shopCats.length === 0 && <p className="text-sm text-ink-muted" data-testid="shop-cats-empty">No categories yet. Add one below — until then, tabs are auto-created from your products' categories.</p>}
            {shopCats.map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`shop-cat-row-${i}`}>
                <input value={c} onChange={(e) => renameShopCat(i, e.target.value)} placeholder="Category name" data-testid={`shop-cat-input-${i}`} className={`${inputCls} flex-1`} />
                <button type="button" onClick={() => moveShopCat(i, -1)} title="Move up" className="grid place-items-center w-9 h-9 rounded-lg border border-parchment hover:border-forest text-ink-muted hover:text-forest">↑</button>
                <button type="button" onClick={() => moveShopCat(i, 1)} title="Move down" className="grid place-items-center w-9 h-9 rounded-lg border border-parchment hover:border-forest text-ink-muted hover:text-forest">↓</button>
                <button type="button" onClick={() => removeShopCat(i)} data-testid={`shop-cat-remove-${i}`} className="grid place-items-center w-9 h-9 rounded-lg border border-parchment hover:border-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button type="button" onClick={addShopCat} data-testid="shop-cat-add" className="inline-flex items-center gap-1 text-forest text-sm font-semibold hover:text-forest-light"><Plus className="w-4 h-4" /> Add category</button>
            <button type="button" onClick={saveShopCats} disabled={shopCatsBusy} data-testid="shop-cats-save" className="ml-auto bg-gold text-night font-semibold px-5 py-2 rounded-md hover:bg-gold-light transition-colors disabled:opacity-60">{shopCatsBusy ? "Saving…" : "Save categories"}</button>
          </div>
        </div>

        {/* SHOP HEADER TEXT */}
        <div className="mb-2 mt-16">
          <h2 className="font-serif text-3xl text-ink">Shop Header Text</h2>
          <p className="text-sm text-ink-muted mt-1">Edit the label, title and intro line shown over the <Link to="/shop" className="text-forest underline">Shop</Link> banner.</p>
        </div>
        <div className="bg-cream border border-parchment rounded-xl p-5 space-y-4 max-w-2xl" data-testid="shop-text-editor">
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Small label (eyebrow)</label>
            <div className="flex gap-2">
              <input value={banners.shop_eyebrow || ""} onChange={(e) => setBanners((s) => ({ ...s, shop_eyebrow: e.target.value }))} placeholder="ÀsÀ Market" data-testid="shop-eyebrow-input" className={`${inputCls} flex-1`} />
              <button onClick={() => saveBanner("shop_eyebrow")} disabled={bannerBusy !== ""} data-testid="save-shop-eyebrow" className="bg-gold text-night font-semibold px-4 rounded-md hover:bg-gold-light transition-colors disabled:opacity-60">Save</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Heading</label>
            <div className="flex gap-2">
              <input value={banners.shop_title || ""} onChange={(e) => setBanners((s) => ({ ...s, shop_title: e.target.value }))} placeholder="Shop" data-testid="shop-title-input" className={`${inputCls} flex-1`} />
              <button onClick={() => saveBanner("shop_title")} disabled={bannerBusy !== ""} data-testid="save-shop-title" className="bg-gold text-night font-semibold px-4 rounded-md hover:bg-gold-light transition-colors disabled:opacity-60">Save</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Intro line (optional)</label>
            <textarea value={banners.shop_intro || ""} onChange={(e) => setBanners((s) => ({ ...s, shop_intro: e.target.value }))} rows={2} placeholder="Authentic Yorùbá textiles, crafts and art — made with heritage." data-testid="shop-intro-input" className={inputCls} />
            <button onClick={() => saveBanner("shop_intro")} disabled={bannerBusy !== ""} data-testid="save-shop-intro" className="mt-2 bg-gold text-night font-semibold px-4 py-2 rounded-md hover:bg-gold-light transition-colors disabled:opacity-60">Save intro</button>
          </div>
        </div>

        {/* LEARNING PORTAL / COURSES MANAGER */}
        <div id="courses" className="flex items-center justify-between mb-2 mt-16 scroll-mt-24">
          <h2 className="font-serif text-3xl text-ink">Courses (Learning Portal)</h2>
          <button onClick={() => setEditingCourse("new")} data-testid="admin-new-course-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> Add Course
          </button>
        </div>
        <p className="text-sm text-ink-muted mb-5">Add and edit language courses for your <Link to="/courses" className="text-forest underline">Learning Portal</Link>. Prices (USD), lessons, banner and level are all editable. Each course has an Enroll button that charges via Stripe/PayPal.</p>
        <div className="space-y-3">
          {courses.length === 0 && <p className="text-sm text-ink-muted" data-testid="admin-courses-empty">No courses yet. Click "Add Course" to create one.</p>}
          {courses.map((c) => (
            <div key={c.id} data-testid={`admin-course-${c.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-lg bg-gold/15 text-gold shrink-0 overflow-hidden">
                {c.banner_image ? <img src={c.banner_image} alt="" className="w-full h-full object-cover" /> : <BookUser className="w-5 h-5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest text-gold">{c.level}</span>
                  {!c.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{c.title}</h3>
                <p className="text-sm text-forest">${Number(c.price).toFixed(2)} · {c.lectures || "—"} · {c.duration || "—"}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditingCourse(c)} data-testid={`admin-edit-course-${c.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => removeCourse(c)} data-testid={`admin-delete-course-${c.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>

        {/* ADVERTS MANAGER */}
        <div id="adverts" className="flex items-center justify-between mb-2 mt-16 scroll-mt-24">
          <h2 className="font-serif text-3xl text-ink">Adverts</h2>
          <button onClick={() => setEditingAdvert("new")} data-testid="admin-new-advert-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> Post Advert
          </button>
        </div>
        <p className="text-sm text-ink-muted mb-5">Post image or video adverts to your <Link to="/adverts" className="text-forest underline">Adverts page</Link>. Add an optional link + button, feature one on the homepage, and see how many clicks each gets.</p>
        <div className="space-y-3">
          {adverts.length === 0 && <p className="text-sm text-ink-muted" data-testid="admin-adverts-empty">No adverts yet. Click "Post Advert" to create one.</p>}
          {adverts.map((a) => (
            <div key={a.id} data-testid={`admin-advert-${a.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="w-16 h-12 rounded-lg bg-ink/5 shrink-0 overflow-hidden grid place-items-center">
                {a.media_url ? (
                  a.media_type === "video"
                    ? <video src={a.media_url} poster={a.poster || undefined} muted className="w-full h-full object-cover" />
                    : <img src={a.media_url} alt="" className="w-full h-full object-cover" />
                ) : <Megaphone className="w-5 h-5 text-ink-muted" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs uppercase tracking-widest text-gold">{a.media_type}</span>
                  {a.featured && <span className="text-xs bg-gold/20 text-gold rounded px-2 py-0.5 font-semibold">★ Featured</span>}
                  {!a.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{a.title || "(untitled advert)"}</h3>
                <p className="text-sm text-ink-muted flex items-center gap-1.5"><MousePointerClick className="w-3.5 h-3.5 text-forest" /> {a.clicks || 0} click{(a.clicks || 0) === 1 ? "" : "s"}{a.link_url ? ` · links to ${a.link_url}` : ""}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditingAdvert(a)} data-testid={`admin-edit-advert-${a.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => removeAdvert(a)} data-testid={`admin-delete-advert-${a.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>

        {/* SHOP / PRODUCTS MANAGER */}
        <div id="shop-products" className="flex items-center justify-between mb-2 mt-16 scroll-mt-24">
          <h2 className="font-serif text-3xl text-ink">Shop Products</h2>
          <button onClick={openNewProduct} data-testid="new-product-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>
        <p className="text-sm text-ink-muted mb-5">Add and edit products for your <Link to="/shop" className="text-forest underline">Shop</Link>. Prices, photos, sizes/variants and stock are all editable.</p>
        <div className="space-y-3">
          {products.length === 0 && <p className="text-sm text-ink-muted" data-testid="admin-products-empty">No products yet. Click "Add Product" to stock your shop.</p>}
          {products.map((p) => (
            <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-lg bg-gold/15 text-gold shrink-0 overflow-hidden">
                {(p.images || [])[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <ShoppingBag className="w-5 h-5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest text-gold">{p.category}</span>
                  {p.featured && <span className="text-xs bg-gold/20 text-gold rounded px-2 py-0.5 font-semibold">Featured</span>}
                  {!p.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{p.name}</h3>
                <p className="text-sm text-forest">${Number(p.price).toFixed(2)}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditProduct(p)} data-testid={`edit-product-${p.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => removeProduct(p)} data-testid={`delete-product-${p.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>

        {/* SHOP ORDERS */}
        <div className="flex items-center justify-between mb-2 mt-16">
          <h2 className="font-serif text-3xl text-ink">Shop Orders</h2>
          {orders.length > 0 && (
            <button
              onClick={() => downloadCsv("orders.csv", [
                { key: "created_at", label: "Date" }, { key: "email", label: "Buyer" },
                { key: "items_summary", label: "Items" }, { key: "amount", label: "Total" },
                { key: "payment_status", label: "Payment" }, { key: "method", label: "Method" },
                { key: "ship_summary", label: "Ship To" },
              ], orders.map((o) => ({
                ...o,
                items_summary: (o.items || []).map((l) => `${l.name} x${l.qty}`).join("; "),
                ship_summary: fmtShip(o.shipping),
              })))}
              data-testid="orders-export-csv"
              className="inline-flex items-center gap-2 border border-forest text-forest text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest hover:text-cream transition-colors"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          )}
        </div>
        <p className="text-sm text-ink-muted mb-5">Every purchase from your <Link to="/shop" className="text-forest underline">Shop</Link>, with buyer email and shipping address. Paid orders trigger an email alert to you.</p>
        <div className="space-y-3" data-testid="admin-orders">
          {orders.length === 0 && <p className="text-sm text-ink-muted" data-testid="admin-orders-empty">No orders yet. They'll appear here as soon as someone checks out.</p>}
          {orders.map((o) => {
            const paid = o.payment_status === "paid";
            return (
              <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-cream border border-parchment rounded-xl p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs rounded px-2 py-0.5 font-semibold ${paid ? "bg-forest/15 text-forest" : "bg-gold/20 text-gold"}`} data-testid={`order-status-${o.id}`}>{paid ? "Paid" : (o.payment_status || "Pending")}</span>
                      <span className="text-xs uppercase tracking-widest text-ink-muted">{o.method}</span>
                      <span className="text-xs text-ink-muted">{fmtDate(o.created_at)}</span>
                    </div>
                    <p className="text-sm text-ink font-medium">{o.email || "No email provided"}</p>
                    <p className="text-sm text-ink-muted mt-1">{(o.items || []).map((l) => `${l.name} × ${l.qty}`).join(", ")}</p>
                    {fmtShip(o.shipping) && <p className="text-xs text-ink-muted mt-1">Ship to: {fmtShip(o.shipping)}</p>}
                  </div>
                  <p className="font-serif text-xl text-forest shrink-0">${Number(o.amount || 0).toFixed(2)}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between mb-2 mt-16">
          <h2 className="font-serif text-3xl text-ink">Events Calendar</h2>
          <button onClick={openNewEvent} data-testid="new-event-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
            <Plus className="w-4 h-4" /> Add Event
          </button>
        </div>
        <p className="text-sm text-ink-muted mb-5">Add concerts, exhibitions and gatherings. Upcoming events show on the public <Link to="/events" className="text-forest underline">Events</Link> page; past dates move to "Past Events" automatically.</p>

        <div className="space-y-3">
          {events.length === 0 && (
            <p className="text-sm text-ink-muted" data-testid="admin-events-empty">No events yet. Click "Add Event" to schedule your first one.</p>
          )}
          {events.map((ev) => (
            <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold shrink-0"><CalendarDays className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest text-gold">{ev.category || "Event"}</span>
                  {ev.date && <span className="text-xs text-ink-muted">· {ev.date}</span>}
                  {!ev.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{ev.title}</h3>
                {ev.location && <p className="text-sm text-ink-muted truncate">{ev.location}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditEvent(ev)} data-testid={`edit-event-${ev.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => removeEvent(ev)} data-testid={`delete-event-${ev.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>

        {/* EVENT REGISTRATIONS */}
        <div id="event-registrations" className="flex items-center justify-between mb-2 mt-16 scroll-mt-24">
          <h2 className="font-serif text-3xl text-ink">Event Registrations</h2>
          <div className="flex items-center gap-3">
            <button onClick={sendAllDueReminders} data-testid="send-due-reminders-button" className="inline-flex items-center gap-2 border border-parchment text-ink text-sm font-semibold px-4 py-2 rounded-full hover:border-forest hover:text-forest transition-colors"><Mail className="w-4 h-4" /> Send due reminders</button>
            <span className="text-sm text-ink-muted" data-testid="registrations-count">{registrations.length} total</span>
          </div>
        </div>
        <p className="text-sm text-ink-muted mb-5">Trip & excursion sign-ups from the Events page. Paid registrations also email you. For Deposit/Instalment plans, set a balance & due date — reminders auto-send at 30, 14 & 7 days before and on the due date, or use the buttons below.</p>
        <div className="space-y-3">
          {registrations.length === 0 && <p className="text-sm text-ink-muted" data-testid="admin-registrations-empty">No registrations yet.</p>}
          {registrations.map((r) => (
            <details key={r.id} data-testid={`admin-registration-${r.id}`} className="bg-cream border border-parchment rounded-xl p-5 group">
              <summary className="flex items-start gap-4 cursor-pointer list-none">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-gold/15 text-gold shrink-0"><Users className="w-5 h-5" /></span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs uppercase tracking-widest text-gold truncate">{r.event_title}</span>
                    <span className={`text-xs rounded px-2 py-0.5 font-semibold ${r.payment_status === "paid" ? "bg-forest/15 text-forest" : r.payment_status === "not_required" ? "bg-ink/10 text-ink-muted" : "bg-gold/20 text-gold"}`}>
                      {r.payment_status === "paid" ? "Paid" : r.payment_status === "not_required" ? "No fee" : "Pending"}
                    </span>
                    {r.amount > 0 && <span className="text-xs text-ink-muted">${Number(r.amount).toFixed(2)}</span>}
                  </div>
                  <h3 className="font-serif text-lg text-ink truncate">{r.full_name} <span className="text-sm text-ink-muted">· {(r.reg_type || "individual") === "group" ? `Group of ${r.headcount}` : "Individual"}</span></h3>
                  <p className="text-sm text-ink-muted truncate">{r.email}{r.phone ? ` · ${r.phone}` : ""}{r.country ? ` · ${r.country}` : ""}</p>
                </div>
                <button onClick={(e) => { e.preventDefault(); removeRegistration(r); }} data-testid={`delete-registration-${r.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
              </summary>
              <div className="mt-4 pt-4 border-t border-parchment grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                {[
                  ["Trip", r.trip], ["Date of birth", r.date_of_birth], ["Gender", r.gender],
                  ["Emergency contact", r.emergency_contact],
                  ["Traveller names", (r.traveller_names || []).join(", ")],
                  ["Needs hotel", r.needs_hotel ? "Yes" : "No"], ["Needs transport", r.needs_transport ? "Yes" : "No"],
                  ["Passport / travel doc", r.passport_note], ["Dietary / medical", r.dietary_medical],
                  ["Attire (style)", r.outfit], ["Heard via", r.heard_from],
                  ["Payment plan", r.payment_plan], ["Message", r.message],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <p key={k} className="text-ink-muted"><span className="text-ink font-medium">{k}:</span> {v}</p>
                ))}
              </div>
              {["Deposit now, balance later", "Instalments (monthly)"].includes(r.payment_plan) && (
                <div className="mt-4 pt-4 border-t border-parchment" data-testid={`balance-controls-${r.id}`}>
                  <p className="text-xs uppercase tracking-widest text-gold mb-2">Balance reminder</p>
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="block text-xs text-ink-muted mb-1">Balance due (USD)</label>
                      <input type="number" min="0" step="0.01" defaultValue={r.balance_amount || ""} onChange={(e) => setBalanceEdits((b) => ({ ...b, [r.id]: { ...b[r.id], balance_amount: e.target.value } }))} data-testid={`balance-amount-${r.id}`} className="w-32 border border-parchment rounded-md px-3 py-2 text-sm bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs text-ink-muted mb-1">Due date</label>
                      <input type="date" defaultValue={r.balance_due_date || ""} onChange={(e) => setBalanceEdits((b) => ({ ...b, [r.id]: { ...b[r.id], balance_due_date: e.target.value } }))} data-testid={`balance-due-${r.id}`} className="border border-parchment rounded-md px-3 py-2 text-sm bg-white" />
                    </div>
                    <button onClick={(e) => { e.preventDefault(); saveBalance(r); }} data-testid={`balance-save-${r.id}`} className="bg-ink text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-ink/90">Save</button>
                    <button onClick={(e) => { e.preventDefault(); sendBalanceReminder(r); }} data-testid={`balance-remind-${r.id}`} className="inline-flex items-center gap-1.5 bg-gold text-night text-sm font-semibold px-4 py-2 rounded-full hover:bg-gold-light"><Mail className="w-3.5 h-3.5" /> Send reminder</button>
                    {r.balance_reminder_sent_at && <span className="text-xs text-forest">Last sent {new Date(r.balance_reminder_sent_at).toLocaleDateString()}</span>}
                  </div>
                </div>
              )}
            </details>
          ))}
        </div>

        {/* EVENT WAITLIST */}
        <div id="event-waitlist" className="flex items-center justify-between mb-2 mt-16 scroll-mt-24">
          <h2 className="font-serif text-3xl text-ink">Event Waitlist</h2>
          <span className="text-sm text-ink-muted">{waitlist.length} total</span>
        </div>
        <p className="text-sm text-ink-muted mb-5">People waiting for a spot on full trips. When a registration is deleted and a spot opens, the earliest person is emailed automatically — or notify anyone manually.</p>
        <div className="space-y-2">
          {waitlist.length === 0 && <p className="text-sm text-ink-muted" data-testid="admin-waitlist-empty">No one on the waitlist.</p>}
          {waitlist.map((w) => (
            <div key={w.id} data-testid={`admin-waitlist-${w.id}`} className="bg-cream border border-parchment rounded-xl p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-widest text-gold truncate">{w.event_title}</span>
                  {w.notified && <span className="text-xs bg-forest/15 text-forest rounded px-2 py-0.5 font-semibold">Notified</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{w.full_name}</h3>
                <p className="text-sm text-ink-muted truncate">{w.email}{w.phone ? ` · ${w.phone}` : ""}</p>
              </div>
              <button onClick={() => notifyWaitlist(w)} data-testid={`waitlist-notify-${w.id}`} className="inline-flex items-center gap-1.5 border border-parchment text-ink text-sm font-semibold px-3 py-2 rounded-full hover:border-forest hover:text-forest transition-colors"><Mail className="w-3.5 h-3.5" /> Notify</button>
              <button onClick={() => removeWaitlist(w)} data-testid={`waitlist-delete-${w.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        {/* TRIP GALLERY MANAGER */}
        <div id="trip-gallery" className="flex items-center justify-between mb-2 mt-16 scroll-mt-24">
          <h2 className="font-serif text-3xl text-ink">Trip Gallery</h2>
          <label className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors cursor-pointer" data-testid="trip-photo-add-label">
            <Plus className="w-4 h-4" /> Add Photo / Video
            <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime,video/ogg" onChange={uploadTripPhoto} data-testid="trip-photo-upload-input" className="hidden" />
          </label>
        </div>
        <p className="text-sm text-ink-muted mb-5">Photos and videos shown in the "Moments from our trips" gallery on the <Link to="/events" className="text-forest underline">Events</Link> page. Drag to reorder; edit a caption and click away to save.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {tripPhotos.length === 0 && <p className="text-sm text-ink-muted col-span-full" data-testid="trip-photos-empty">No trip photos yet. Click "Add Photo".</p>}
          {tripPhotos.map((p, idx) => (
            <div
              key={p.id}
              data-testid={`admin-trip-photo-${p.id}`}
              draggable
              onDragStart={() => { dragTripRef.current = idx; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleTripDrop(idx); }}
              className="relative rounded-lg overflow-hidden border border-parchment bg-cream group cursor-move"
            >
              {p.media_type === "video" ? (
                p.poster
                  ? <img src={p.poster} alt="" className="w-full h-28 object-cover" />
                  : <video src={p.image_url} muted playsInline className="w-full h-28 object-cover pointer-events-none" />
              ) : (
                <img src={p.image_url} alt="" className="w-full h-28 object-cover" />
              )}
              {p.media_type === "video" && <span className="absolute bottom-9 left-2 grid place-items-center px-2 h-6 rounded-full bg-night/70 text-cream text-[10px] font-semibold uppercase tracking-wide">Video</span>}
              {p.media_type === "video" && (
                <div className="absolute bottom-9 right-2 flex items-center gap-1">
                  <label title="Upload a cover image" data-testid={`trip-poster-label-${p.id}`} className="grid place-items-center px-2 h-6 rounded-full bg-night/70 text-cream text-[10px] font-semibold cursor-pointer hover:bg-forest transition-colors">
                    {p.poster ? "Change cover" : "Set cover"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => uploadTripPoster(p, e)} data-testid={`trip-poster-input-${p.id}`} className="hidden" />
                  </label>
                  {p.poster && <button onClick={() => removeTripPoster(p)} title="Remove cover" data-testid={`trip-poster-clear-${p.id}`} className="grid place-items-center w-6 h-6 rounded-full bg-night/70 text-cream hover:bg-red-500 transition-colors"><X className="w-3 h-3" /></button>}
                </div>
              )}
              <button onClick={() => removeTripPhoto(p)} data-testid={`delete-trip-photo-${p.id}`} className="absolute top-2 right-2 grid place-items-center w-8 h-8 rounded-full bg-night/70 text-cream hover:bg-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
              <span className="absolute top-2 left-2 grid place-items-center w-7 h-7 rounded-full bg-night/60 text-cream" title="Drag to reorder"><GripVertical className="w-3.5 h-3.5" /></span>
              <input
                defaultValue={p.caption || ""}
                onBlur={(e) => saveTripCaption(p, e.target.value)}
                placeholder="Add a caption…"
                data-testid={`trip-caption-${p.id}`}
                className="w-full border-t border-parchment px-2 py-1.5 text-xs text-ink bg-white focus:outline-none focus:bg-sand"
              />
            </div>
          ))}
        </div>

        {/* GALLERY MANAGER */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2 mt-16">
          <h2 className="font-serif text-3xl text-ink">Gallery</h2>
          <div className="flex flex-wrap items-center gap-2">
            <select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} data-testid="gallery-bulk-category" className="text-sm border border-parchment rounded-full px-4 py-2 bg-cream">
              {GALLERY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <label data-testid="gallery-bulk-upload-label" className="inline-flex items-center gap-2 border border-forest text-forest font-semibold px-4 py-2.5 rounded-full hover:bg-forest hover:text-cream transition-colors cursor-pointer">
              {bulkProgress ? `Uploading ${bulkProgress.done}/${bulkProgress.total}…` : "Bulk Upload"}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={bulkUploadGallery} disabled={!!bulkProgress} data-testid="gallery-bulk-upload-input" className="hidden" />
            </label>
            <button onClick={openNewGallery} data-testid="new-gallery-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
              <Plus className="w-4 h-4" /> Add Photo
            </button>
          </div>
        </div>
        <p className="text-sm text-ink-muted mb-5">Drag any photo to reorder how it appears on the public gallery. Bulk-uploaded photos go into the category selected above.</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {galleryItems.map((g, idx) => (
            <div
              key={g.id}
              data-testid={`admin-gallery-${g.id}`}
              draggable
              onDragStart={() => { dragIndexRef.current = idx; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleGalleryDrop(idx); }}
              className="relative group rounded-xl overflow-hidden border border-parchment bg-cream cursor-move"
            >
              {g.type === "video" ? (
                <div className="relative w-full h-32 bg-night">
                  <video src={g.src} poster={g.poster || undefined} muted playsInline className="w-full h-32 object-cover pointer-events-none" />
                  <span className="absolute inset-0 grid place-items-center text-cream/90 pointer-events-none"><Play className="w-6 h-6" /></span>
                </div>
              ) : (
                <img src={g.src} alt={g.caption} className="w-full h-32 object-cover pointer-events-none" loading="lazy" />
              )}
              {!g.published && <span className="absolute top-2 left-2 text-[10px] bg-ink/70 text-cream rounded px-1.5 py-0.5">Hidden</span>}
              <span className="absolute bottom-14 left-2 grid place-items-center w-6 h-6 rounded bg-night/50 text-cream opacity-0 group-hover:opacity-100 transition-opacity"><GripVertical className="w-3.5 h-3.5" /></span>
              <div className="p-2">
                <p className="text-[10px] uppercase tracking-widest text-gold truncate">{g.category}</p>
                <p className="text-xs text-ink truncate">{g.caption || "—"}</p>
              </div>
              <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEditGallery(g)} data-testid={`edit-gallery-${g.id}`} className="grid place-items-center w-8 h-8 rounded-lg bg-cream/90 border border-parchment hover:border-forest hover:text-forest transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => removeGallery(g)} data-testid={`delete-gallery-${g.id}`} className="grid place-items-center w-8 h-8 rounded-lg bg-cream/90 border border-parchment hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>



        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 mt-16">
          <h2 className="font-serif text-3xl text-ink">Yorùbá Names</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                value={namesQuery}
                onChange={(e) => { setNamesQuery(e.target.value); loadNames(e.target.value); }}
                placeholder="Search names…"
                data-testid="admin-names-search"
                className="rounded-full border border-parchment bg-cream pl-4 pr-4 py-2 text-sm text-ink focus:outline-none focus:border-forest"
              />
            </div>
            <button onClick={openNewName} data-testid="new-name-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
              <Plus className="w-4 h-4" /> New Name
            </button>
          </div>
        </div>

        <p className="text-sm text-ink-muted mb-4">{namesTotal.toLocaleString()} names total{namesQuery ? " (filtered)" : ""} · showing {namesList.length}</p>
        <div className="grid gap-3 md:grid-cols-2">
          {namesList.map((n) => (
            <div key={n.id} data-testid={`admin-name-${n.id}`} className="bg-cream border border-parchment rounded-xl p-4 flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-full bg-gold/15 text-gold shrink-0 text-sm font-semibold">{n.letter}</span>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-lg text-ink truncate">{n.name}</h3>
                {(n.gender || n.meaning) && <p className="text-xs text-ink-muted truncate">{[n.gender, n.meaning].filter(Boolean).join(" · ")}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditName(n)} data-testid={`edit-name-${n.id}`} className="grid place-items-center w-9 h-9 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => removeName(n)} data-testid={`delete-name-${n.id}`} className="grid place-items-center w-9 h-9 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* QUIZ MANAGER */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 mt-16">
          <h2 className="font-serif text-3xl text-ink">Quiz Questions (Ìdánwò)</h2>
          <div className="flex items-center gap-3">
            <button onClick={() => setBulkOpen(true)} data-testid="bulk-quiz-button" className="inline-flex items-center gap-2 border border-forest text-forest font-semibold px-5 py-2.5 rounded-full hover:bg-forest hover:text-cream transition-colors">
              <ListPlus className="w-4 h-4" /> Bulk Add
            </button>
            <button onClick={openNewQuiz} data-testid="new-quiz-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
              <Plus className="w-4 h-4" /> New Question
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {quiz.length === 0 && <p className="text-sm text-ink-muted">No quiz questions yet. Add your first one!</p>}
          {quiz.map((q) => (
            <div key={q.id} data-testid={`admin-quiz-${q.id}`} className="bg-cream border border-parchment rounded-xl p-5 flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-forest/10 text-forest shrink-0"><Brain className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs uppercase tracking-widest text-gold">{q.category}</span>
                  <span className="text-[10px] uppercase tracking-wide bg-forest/10 text-forest rounded px-2 py-0.5">{q.type === "boolean" ? "True / False" : "Multiple choice"}</span>
                  {!q.published && <span className="text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
                </div>
                <h3 className="font-serif text-lg text-ink truncate">{q.question}</h3>
                <p className="text-sm text-ink-muted truncate">Answer: {q.options?.[q.correct_index]}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEditQuiz(q)} data-testid={`edit-quiz-${q.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-forest hover:text-forest transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => removeQuiz(q)} data-testid={`delete-quiz-${q.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-parchment hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* MONTHLY DONORS / SUBSCRIPTIONS */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 mt-16">
          <h2 className="font-serif text-3xl text-ink">Monthly Donors</h2>
          <span className="text-sm text-ink-muted">{subscriptions.filter((s) => s.status === "active").length} active · {subscriptions.length} total</span>
        </div>
        {subscriptions.length === 0 ? (
          <p className="text-sm text-ink-muted" data-testid="subscriptions-empty">No monthly donors yet. Recurring PayPal donations will appear here.</p>
        ) : (
          <div className="overflow-x-auto border border-parchment rounded-xl" data-testid="subscriptions-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sand text-left text-ink-muted">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Started</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((s) => (
                  <tr key={s.id} data-testid={`subscription-${s.paypal_subscription_id}`} className="border-t border-parchment">
                    <td className="px-4 py-3 text-ink">{s.name || "—"}</td>
                    <td className="px-4 py-3 text-ink">{s.email || "—"}</td>
                    <td className="px-4 py-3 text-ink">{s.amount}/mo</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${s.status === "active" ? "bg-forest/10 text-forest" : "bg-ink/10 text-ink-muted"}`}>
                        {s.status === "active" ? "Active" : "Cancelled"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-muted whitespace-nowrap">{s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {s.status === "active" && (
                        <button
                          onClick={() => cancelSubscription(s)}
                          disabled={cancelling === s.paypal_subscription_id}
                          data-testid={`cancel-subscription-${s.paypal_subscription_id}`}
                          className="inline-flex items-center gap-1.5 border border-destructive text-destructive text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-destructive hover:text-cream transition-colors disabled:opacity-60"
                        >
                          {cancelling === s.paypal_subscription_id ? "Cancelling..." : "Cancel"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={close} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editing === "new" ? "New Article" : "Edit Article"}</h3>
              <button onClick={close} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <Field label="Title"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="article-title-input" className={inputCls} /></Field>
              <Field label="Excerpt"><textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} data-testid="article-excerpt-input" className={inputCls} /></Field>
              <Field label="Cover Image (optional)">
                <div className="flex flex-wrap items-center gap-3">
                  <label
                    data-testid="article-cover-upload-label"
                    className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer"
                  >
                    {uploadingCover ? "Uploading…" : "Upload image"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={uploadArticleCover}
                      disabled={uploadingCover}
                      data-testid="article-cover-upload-input"
                      className="hidden"
                    />
                  </label>
                  <span className="text-xs text-ink-muted">or paste a URL below</span>
                </div>
                <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://…" data-testid="article-cover-input" className={`${inputCls} mt-2`} />
              </Field>
              {form.cover_image && <img src={form.cover_image} alt="preview" className="w-full h-40 object-cover rounded-lg border border-parchment" />}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category"><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="article-category-input" className={inputCls} /></Field>
                <Field label="Tags (comma separated)"><input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="article-tags-input" className={inputCls} /></Field>
              </div>
              <Field label="Content (Markdown: ## heading, - list, **bold**)"><textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12} data-testid="article-content-input" className={inputCls + " font-mono text-sm"} /></Field>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="article-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={saving} data-testid="save-article-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {saving ? "Saving..." : "Save Article"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Proverb editor drawer */}
      {editingProverb && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeProverb} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingProverb === "new" ? "New Proverb" : "Edit Proverb"}</h3>
              <button onClick={closeProverb} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveProverb} className="p-6 space-y-4">
              <Field label="Proverb (Yorùbá)"><textarea required value={proverbForm.yoruba} onChange={(e) => setProverbForm({ ...proverbForm, yoruba: e.target.value })} rows={3} data-testid="proverb-yoruba-input" className={inputCls} /></Field>
              <Field label="Literal translation (optional)"><textarea value={proverbForm.translation} onChange={(e) => setProverbForm({ ...proverbForm, translation: e.target.value })} rows={2} placeholder="Word-for-word English translation" data-testid="proverb-translation-input" className={inputCls} /></Field>
              <Field label="Meaning"><textarea value={proverbForm.meaning} onChange={(e) => setProverbForm({ ...proverbForm, meaning: e.target.value })} rows={3} data-testid="proverb-meaning-input" className={inputCls} /></Field>
              <Field label="Explanation / Story (optional — the deeper wisdom)"><textarea value={proverbForm.explanation} onChange={(e) => setProverbForm({ ...proverbForm, explanation: e.target.value })} rows={5} data-testid="proverb-explanation-input" className={inputCls} /></Field>
              <Field label="Usage / Context (optional — when it's said)"><textarea value={proverbForm.usage} onChange={(e) => setProverbForm({ ...proverbForm, usage: e.target.value })} rows={3} data-testid="proverb-usage-input" className={inputCls} /></Field>
              <Field label="Theme"><input value={proverbForm.theme} onChange={(e) => setProverbForm({ ...proverbForm, theme: e.target.value })} data-testid="proverb-theme-input" className={inputCls} /></Field>
              <Field label="Image (optional)">
                <div className="flex flex-wrap items-center gap-3">
                  <label data-testid="proverb-image-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                    {uploadingProverb ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadProverbImage} disabled={uploadingProverb} data-testid="proverb-image-upload-input" className="hidden" />
                  </label>
                  <span className="text-xs text-ink-muted">or paste a URL below</span>
                </div>
                <input value={proverbForm.image} onChange={(e) => setProverbForm({ ...proverbForm, image: e.target.value })} placeholder="https://…" data-testid="proverb-image-input" className={`${inputCls} mt-2`} />
              </Field>
              {proverbForm.image && <img src={proverbForm.image} alt="preview" className="w-full h-40 object-cover rounded-lg border border-parchment" />}
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={proverbForm.published} onChange={(e) => setProverbForm({ ...proverbForm, published: e.target.checked })} data-testid="proverb-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingProverb} data-testid="save-proverb-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingProverb ? "Saving..." : "Save Proverb"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Daily question editor drawer */}
      {editingQuestion && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeQuestion} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingQuestion === "new" ? "New Daily Question" : "Edit Daily Question"}</h3>
              <button onClick={closeQuestion} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveQuestion} className="p-6 space-y-4">
              <Field label="Question (Yorùbá / English)"><textarea required value={questionForm.question} onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })} rows={4} data-testid="daily-question-question-input" className={inputCls} /></Field>
              <Field label="Answer / Meaning (hidden until revealed)"><textarea value={questionForm.answer} onChange={(e) => setQuestionForm({ ...questionForm, answer: e.target.value })} rows={5} data-testid="daily-question-answer-input" className={inputCls} /></Field>
              <Field label="Card caption (custom text on the Question Card — optional)"><textarea value={questionForm.card_caption} onChange={(e) => setQuestionForm({ ...questionForm, card_caption: e.target.value })} rows={2} placeholder="Kíló túnmọ̀ sí? · What does it mean? Reflect, then discover the answer." data-testid="daily-question-caption-input" className={inputCls} /></Field>
              <Field label="Image (optional — shows on the public card + share cards)">
                <div className="flex flex-wrap items-center gap-3">
                  <label data-testid="daily-question-image-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                    {uploadingQuestion ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadQuestionImage} disabled={uploadingQuestion} data-testid="daily-question-image-upload-input" className="hidden" />
                  </label>
                  <span className="text-xs text-ink-muted">or paste a URL below</span>
                </div>
                <input value={questionForm.image} onChange={(e) => setQuestionForm({ ...questionForm, image: e.target.value })} placeholder="https://…" data-testid="daily-question-image-input" className={`${inputCls} mt-2`} />
              </Field>
              {questionForm.image && <img src={questionForm.image} alt="preview" className="w-full h-40 object-cover rounded-lg border border-parchment" />}
              <Field label="Theme"><input value={questionForm.theme} onChange={(e) => setQuestionForm({ ...questionForm, theme: e.target.value })} data-testid="daily-question-theme-input" className={inputCls} /></Field>
              <label className="flex items-start gap-2 text-sm text-ink">
                <input type="checkbox" checked={questionForm.answer_visible} onChange={(e) => setQuestionForm({ ...questionForm, answer_visible: e.target.checked })} data-testid="daily-question-answer-visible-checkbox" className="mt-1 w-4 h-4 accent-forest" />
                <span>Reveal answer publicly<span className="block text-xs text-ink-muted">Shows the meaning + the "Answer Card" download to visitors. Leave off to post the question first, then reveal after people have guessed.</span></span>
              </label>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={questionForm.published} onChange={(e) => setQuestionForm({ ...questionForm, published: e.target.checked })} data-testid="daily-question-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingQuestion} data-testid="save-daily-question-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingQuestion ? "Saving..." : "Save Question"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Leader editor drawer */}
      {editingLeader && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeLeader} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingLeader === "new" ? "New Person" : "Edit Person"}</h3>
              <button onClick={closeLeader} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveLeader} className="p-6 space-y-4">
              <Field label="Full name"><input required value={leaderForm.name} onChange={(e) => setLeaderForm({ ...leaderForm, name: e.target.value })} data-testid="leader-name-input" className={inputCls} /></Field>
              <Field label="Title / Role"><input value={leaderForm.title} onChange={(e) => setLeaderForm({ ...leaderForm, title: e.target.value })} placeholder="e.g. Executive Director" data-testid="leader-title-input" className={inputCls} /></Field>
              <Field label="Group (choose one or type your own custom group)">
                <input list="leader-groups" value={leaderForm.group} onChange={(e) => setLeaderForm({ ...leaderForm, group: e.target.value })} placeholder="e.g. Leaders, Directors, Advisors…" data-testid="leader-group-input" className={inputCls} />
                <datalist id="leader-groups">
                  {LEADER_GROUP_SUGGESTIONS.map((g) => <option key={g} value={g} />)}
                </datalist>
              </Field>
              <Field label="Biography (optional)"><textarea value={leaderForm.bio} onChange={(e) => setLeaderForm({ ...leaderForm, bio: e.target.value })} rows={5} data-testid="leader-bio-input" className={inputCls} /></Field>
              <Field label="Photo (optional)">
                <div className="flex flex-wrap items-center gap-3">
                  <label data-testid="leader-image-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                    {uploadingLeader ? "Uploading…" : "Upload photo"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadLeaderImage} disabled={uploadingLeader} data-testid="leader-image-upload-input" className="hidden" />
                  </label>
                  <span className="text-xs text-ink-muted">or paste a URL below</span>
                </div>
                <input value={leaderForm.image} onChange={(e) => setLeaderForm({ ...leaderForm, image: e.target.value })} placeholder="https://…" data-testid="leader-image-input" className={`${inputCls} mt-2`} />
              </Field>
              {leaderForm.image && <img src={leaderForm.image} alt="preview" className="w-40 h-48 object-cover rounded-lg border border-parchment" />}
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={leaderForm.published} onChange={(e) => setLeaderForm({ ...leaderForm, published: e.target.checked })} data-testid="leader-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingLeader} data-testid="save-leader-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingLeader ? "Saving..." : "Save Person"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Museum editor drawer */}
      {editingMuseum && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeMuseum} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingMuseum === "new" ? "New Institution" : "Edit Institution"}</h3>
              <button onClick={closeMuseum} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveMuseum} className="p-6 space-y-4">
              <Field label="Name"><input required value={museumForm.name} onChange={(e) => setMuseumForm({ ...museumForm, name: e.target.value })} data-testid="museum-name-input" className={inputCls} /></Field>
              <Field label="Location (City, Country)"><input value={museumForm.location} onChange={(e) => setMuseumForm({ ...museumForm, location: e.target.value })} data-testid="museum-location-input" className={inputCls} /></Field>
              <Field label="Type">
                <select value={museumForm.type} onChange={(e) => setMuseumForm({ ...museumForm, type: e.target.value })} data-testid="museum-type-input" className={inputCls}>
                  <option>Museum</option>
                  <option>Heritage Site</option>
                  <option>Gallery</option>
                  <option>Archive</option>
                  <option>Cultural Center</option>
                </select>
              </Field>
              <Field label="Description"><textarea value={museumForm.description} onChange={(e) => setMuseumForm({ ...museumForm, description: e.target.value })} rows={5} data-testid="museum-description-input" className={inputCls} /></Field>
              <Field label="Website (optional)"><input value={museumForm.website} onChange={(e) => setMuseumForm({ ...museumForm, website: e.target.value })} placeholder="https://…" data-testid="museum-website-input" className={inputCls} /></Field>
              <Field label="Image (optional)">
                <div className="flex flex-wrap items-center gap-3">
                  <label
                    data-testid="museum-image-upload-label"
                    className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer disabled:opacity-60"
                  >
                    {uploadingImage ? "Uploading…" : "Upload image"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={uploadMuseumImage}
                      disabled={uploadingImage}
                      data-testid="museum-image-upload-input"
                      className="hidden"
                    />
                  </label>
                  <span className="text-xs text-ink-muted">or paste a URL below</span>
                </div>
                <input value={museumForm.image} onChange={(e) => setMuseumForm({ ...museumForm, image: e.target.value })} placeholder="https://…" data-testid="museum-image-input" className={`${inputCls} mt-2`} />
              </Field>
              {museumForm.image && <img src={museumForm.image} alt="preview" className="w-full h-40 object-cover rounded-lg border border-parchment" />}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Latitude (for map pin)"><input type="number" step="any" value={museumForm.lat} onChange={(e) => setMuseumForm({ ...museumForm, lat: e.target.value })} placeholder="e.g. 6.4489" data-testid="museum-lat-input" className={inputCls} /></Field>
                <Field label="Longitude (for map pin)"><input type="number" step="any" value={museumForm.lng} onChange={(e) => setMuseumForm({ ...museumForm, lng: e.target.value })} placeholder="e.g. 3.4053" data-testid="museum-lng-input" className={inputCls} /></Field>
              </div>
              <p className="text-xs text-ink-muted -mt-2">Tip: find coordinates by right-clicking a spot in Google Maps → the lat, lng appears at the top. Leave blank to hide from the map.</p>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={museumForm.published} onChange={(e) => setMuseumForm({ ...museumForm, published: e.target.checked })} data-testid="museum-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingMuseum} data-testid="save-museum-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingMuseum ? "Saving..." : "Save Institution"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Òrìṣà editor drawer */}
      {editingOrisa && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeOrisa} />
          <div className="relative w-full max-w-2xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingOrisa === "new" ? "New Òrìṣà" : "Edit Òrìṣà"}</h3>
              <button onClick={closeOrisa} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveOrisa} className="p-6 space-y-4">
              <Field label="Name"><input required value={orisaForm.name} onChange={(e) => setOrisaForm({ ...orisaForm, name: e.target.value })} data-testid="orisa-name-input" className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Domain / Title"><input value={orisaForm.domain} onChange={(e) => setOrisaForm({ ...orisaForm, domain: e.target.value })} placeholder="Thunder, Justice…" data-testid="orisa-domain-input" className={inputCls} /></Field>
                <Field label="Colour"><input value={orisaForm.color} onChange={(e) => setOrisaForm({ ...orisaForm, color: e.target.value })} placeholder="Red & White" data-testid="orisa-color-input" className={inputCls} /></Field>
              </div>
              <Field label="Subtitle (under hero title)"><input value={orisaForm.subtitle} onChange={(e) => setOrisaForm({ ...orisaForm, subtitle: e.target.value })} placeholder="The Thunderous King of Yorùbáland" data-testid="orisa-subtitle-input" className={inputCls} /></Field>
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Hero / card image</label>
                <label data-testid="orisa-image-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                  {uploadingOrisa ? "Uploading…" : "Upload image"}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadOrisaImage} disabled={uploadingOrisa} data-testid="orisa-image-upload-input" className="hidden" />
                </label>
                <input value={orisaForm.image} onChange={(e) => setOrisaForm({ ...orisaForm, image: e.target.value })} placeholder="…or paste an image URL" data-testid="orisa-image-input" className={`${inputCls} mt-2`} />
                {orisaForm.image && <img src={orisaForm.image} alt="" className="mt-2 h-32 w-full object-cover rounded-lg border border-parchment" />}
              </div>
              <Field label="Short description (grid card)"><textarea value={orisaForm.description} onChange={(e) => setOrisaForm({ ...orisaForm, description: e.target.value })} rows={3} data-testid="orisa-description-input" className={inputCls} /></Field>
              <Field label="Intro pullquote (top of detail page)"><textarea value={orisaForm.intro} onChange={(e) => setOrisaForm({ ...orisaForm, intro: e.target.value })} rows={3} data-testid="orisa-intro-input" className={inputCls} /></Field>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-ink">Detail sections</label>
                  <button type="button" onClick={addSection} data-testid="orisa-add-section" className="inline-flex items-center gap-1 text-forest text-sm font-semibold hover:text-forest-light"><Plus className="w-4 h-4" /> Add section</button>
                </div>
                <div className="space-y-3">
                  {(orisaForm.sections || []).length === 0 && <p className="text-sm text-ink-muted">No sections yet. Add one to build the detail page.</p>}
                  {(orisaForm.sections || []).map((s, i) => (
                    <div key={i} data-testid={`orisa-section-editor-${i}`} className="border border-parchment rounded-lg p-3 bg-white">
                      <div className="flex items-center gap-2 mb-2">
                        <input value={s.heading} onChange={(e) => updateSection(i, "heading", e.target.value)} placeholder={`Section ${i + 1} heading`} data-testid={`orisa-section-heading-${i}`} className={`${inputCls} flex-1`} />
                        <button type="button" onClick={() => moveSection(i, -1)} className="grid place-items-center w-9 h-9 rounded-lg border border-parchment hover:border-forest text-ink-muted hover:text-forest" title="Move up">↑</button>
                        <button type="button" onClick={() => moveSection(i, 1)} className="grid place-items-center w-9 h-9 rounded-lg border border-parchment hover:border-forest text-ink-muted hover:text-forest" title="Move down">↓</button>
                        <button type="button" onClick={() => removeSection(i)} data-testid={`orisa-section-remove-${i}`} className="grid place-items-center w-9 h-9 rounded-lg border border-parchment hover:border-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <textarea value={s.body} onChange={(e) => updateSection(i, "body", e.target.value)} rows={4} placeholder="Section text. Leave a blank line between paragraphs." data-testid={`orisa-section-body-${i}`} className={inputCls} />
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <label data-testid={`orisa-section-image-upload-label-${i}`} className="inline-flex items-center gap-2 border border-forest text-forest text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-forest hover:text-cream transition-colors cursor-pointer">
                          {uploadingSection === i ? "Uploading…" : "Add image"}
                          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => uploadSectionImage(i, e)} disabled={uploadingSection === i} data-testid={`orisa-section-image-upload-${i}`} className="hidden" />
                        </label>
                        <input value={s.image || ""} onChange={(e) => updateSection(i, "image", e.target.value)} placeholder="…or paste image URL (optional)" data-testid={`orisa-section-image-input-${i}`} className={`${inputCls} flex-1 min-w-[180px] text-sm`} />
                      </div>
                      {s.image && (
                        <div className="mt-2 relative inline-block">
                          <img src={s.image} alt="" className="h-24 rounded-lg border border-parchment object-cover" />
                          <button type="button" onClick={() => updateSection(i, "image", "")} data-testid={`orisa-section-image-clear-${i}`} className="absolute -top-2 -right-2 grid place-items-center w-6 h-6 rounded-full bg-night text-cream text-xs"><X className="w-3 h-3" /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={orisaForm.published} onChange={(e) => setOrisaForm({ ...orisaForm, published: e.target.checked })} data-testid="orisa-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingOrisa} data-testid="save-orisa-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingOrisa ? "Saving..." : "Save Òrìṣà"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Course editor drawer */}
      {editingCourse && <CourseEditorModal course={editingCourse} onClose={() => setEditingCourse(null)} onSaved={reloadCourses} />}
      {editingAdvert && <AdvertEditorModal advert={editingAdvert} onClose={() => setEditingAdvert(null)} onSaved={reloadAdverts} />}

      {/* Product editor drawer */}
      {editingProduct && (        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeProduct} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingProduct === "new" ? "New Product" : "Edit Product"}</h3>
              <button onClick={closeProduct} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveProduct} className="p-6 space-y-4">
              <Field label="Product name"><input required value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} data-testid="product-name-input" className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Price (USD)"><input type="number" step="0.01" min="0" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} data-testid="product-price-input" className={inputCls} /></Field>
                <Field label="Category"><input value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} data-testid="product-category-input" className={inputCls} /></Field>
              </div>
              <Field label="Description"><textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} rows={4} data-testid="product-description-input" className={inputCls} /></Field>
              <Field label="Photos">
                <label data-testid="product-image-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                  {uploadingProduct ? "Uploading…" : "Upload photo(s)"}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={uploadProductImage} disabled={uploadingProduct} data-testid="product-image-upload-input" className="hidden" />
                </label>
                {(productForm.images || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {productForm.images.map((img, i) => (
                      <div key={i} className="relative">
                        <img src={img} alt="" className="w-20 h-20 object-cover rounded-lg border border-parchment" />
                        <button type="button" onClick={() => setProductForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))} className="absolute -top-2 -right-2 grid place-items-center w-6 h-6 rounded-full bg-destructive text-white"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </Field>
              <Field label="Sizes / Variants (one per line, e.g. 'Size: S, M, L')"><textarea value={variantsText} onChange={(e) => setVariantsText(e.target.value)} rows={3} placeholder={"Size: S, M, L\nColour: Indigo, Gold"} data-testid="product-variants-input" className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Stock (-1 = unlimited)"><input type="number" value={productForm.stock} onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })} data-testid="product-stock-input" className={inputCls} /></Field>
                <label className="flex items-center gap-2 text-sm text-ink mt-7">
                  <input type="checkbox" checked={productForm.requires_shipping} onChange={(e) => setProductForm({ ...productForm, requires_shipping: e.target.checked })} data-testid="product-shipping-checkbox" className="w-4 h-4 accent-forest" />
                  Needs shipping
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={productForm.published} onChange={(e) => setProductForm({ ...productForm, published: e.target.checked })} data-testid="product-published-checkbox" className="w-4 h-4 accent-forest" />
                  Published
                </label>
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={!!productForm.featured} onChange={(e) => setProductForm({ ...productForm, featured: e.target.checked })} data-testid="product-featured-checkbox" className="w-4 h-4 accent-gold" />
                  Feature on homepage
                </label>
              </div>
              <button type="submit" disabled={savingProduct} data-testid="save-product-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingProduct ? "Saving..." : "Save Product"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Event editor drawer */}
      {editingEvent && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeEvent} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingEvent === "new" ? "New Event" : "Edit Event"}</h3>
              <button onClick={closeEvent} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveEvent} className="p-6 space-y-4">
              <Field label="Event title"><input required value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} data-testid="event-title-input" className={inputCls} /></Field>
              <Field label="Category">
                <select value={eventForm.category} onChange={(e) => setEventForm({ ...eventForm, category: e.target.value })} data-testid="event-category-input" className={inputCls}>
                  {EVENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>

              <div className="rounded-lg border-2 border-gold/50 bg-gold/10 p-4 space-y-3">
                <label className="flex items-start gap-2 text-sm font-semibold text-ink cursor-pointer">
                  <input type="checkbox" checked={eventForm.requires_registration} onChange={(e) => setEventForm({ ...eventForm, requires_registration: e.target.checked })} data-testid="event-requires-registration-checkbox" className="w-5 h-5 accent-gold mt-0.5" />
                  <span>✈️ Make this a TRIP / EXCURSION with a sign-up form
                    <span className="block text-xs text-ink-muted font-normal mt-0.5">Turn this on to show a full trip application &amp; registration form on the Events page, collect a deposit or fee (Stripe / PayPal / Paystack), set capacity, add trip FAQs and manage a waitlist.</span>
                  </span>
                </label>
                {eventForm.requires_registration && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Registration fee / deposit (USD)"><input type="number" min="0" step="0.01" value={eventForm.registration_fee} onChange={(e) => setEventForm({ ...eventForm, registration_fee: e.target.value })} data-testid="event-registration-fee-input" className={inputCls} /></Field>
                      <Field label="Fee label"><input value={eventForm.fee_label} onChange={(e) => setEventForm({ ...eventForm, fee_label: e.target.value })} placeholder="Registration Fee / Deposit" data-testid="event-fee-label-input" className={inputCls} /></Field>
                    </div>
                    <Field label="Note shown above the form (optional)"><textarea value={eventForm.registration_note} onChange={(e) => setEventForm({ ...eventForm, registration_note: e.target.value })} rows={2} placeholder="e.g. A £150 deposit secures your place; balance due 8 weeks before departure." data-testid="event-registration-note-input" className={inputCls} /></Field>
                    <Field label="Capacity / max spots (0 = unlimited)"><input type="number" min="0" step="1" value={eventForm.capacity} onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value })} data-testid="event-capacity-input" className={inputCls} /></Field>
                    <p className="text-xs text-ink-muted">Set the fee to 0 to collect registrations without an online payment. Set a capacity to show "only X spots left" and close the form when full.</p>
                    <div className="pt-2">
                      <label className="block text-sm font-medium text-ink mb-1.5">Trip FAQs</label>
                      <div className="space-y-2">
                        {(eventForm.faqs || []).map((fq, i) => (
                          <div key={i} className="rounded-lg border border-parchment bg-white p-3 space-y-2" data-testid={`faq-row-${i}`}>
                            <div className="flex items-center gap-2">
                              <input value={fq.q} onChange={(e) => { const a = [...eventForm.faqs]; a[i] = { ...a[i], q: e.target.value }; setEventForm({ ...eventForm, faqs: a }); }} placeholder="Question" data-testid={`faq-q-${i}`} className={inputCls} />
                              <button type="button" onClick={() => setEventForm({ ...eventForm, faqs: eventForm.faqs.filter((_, j) => j !== i) })} data-testid={`faq-remove-${i}`} className="grid place-items-center w-9 h-9 rounded-lg border border-parchment hover:border-destructive hover:text-destructive shrink-0"><X className="w-4 h-4" /></button>
                            </div>
                            <textarea value={fq.a} onChange={(e) => { const a = [...eventForm.faqs]; a[i] = { ...a[i], a: e.target.value }; setEventForm({ ...eventForm, faqs: a }); }} rows={2} placeholder="Answer" data-testid={`faq-a-${i}`} className={inputCls} />
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={() => setEventForm({ ...eventForm, faqs: [...(eventForm.faqs || []), { q: "", a: "" }] })} data-testid="faq-add-button" className="mt-2 inline-flex items-center gap-1.5 text-sm text-forest font-semibold hover:underline"><Plus className="w-4 h-4" /> Add FAQ</button>
                    </div>
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date"><input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} data-testid="event-date-input" className={inputCls} /></Field>
                <Field label="Time"><input value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} placeholder="e.g. 7:00 PM" data-testid="event-time-input" className={inputCls} /></Field>
              </div>
              <Field label="Location"><input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder="Venue, City" data-testid="event-location-input" className={inputCls} /></Field>
              <Field label="Description"><textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} rows={5} data-testid="event-description-input" className={inputCls} /></Field>
              <Field label="Tickets / Details link (optional)"><input value={eventForm.link} onChange={(e) => setEventForm({ ...eventForm, link: e.target.value })} placeholder="https://…" data-testid="event-link-input" className={inputCls} /></Field>
              <Field label="Image (optional)">
                <div className="flex flex-wrap items-center gap-3">
                  <label data-testid="event-image-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                    {uploadingEvent ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadEventImage} disabled={uploadingEvent} data-testid="event-image-upload-input" className="hidden" />
                  </label>
                  <span className="text-xs text-ink-muted">or paste a URL below</span>
                </div>
                <input value={eventForm.image} onChange={(e) => setEventForm({ ...eventForm, image: e.target.value })} placeholder="https://…" data-testid="event-image-input" className={`${inputCls} mt-2`} />
              </Field>
              {eventForm.image && <img src={eventForm.image} alt="preview" className="w-full h-40 object-cover rounded-lg border border-parchment" />}

              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={eventForm.published} onChange={(e) => setEventForm({ ...eventForm, published: e.target.checked })} data-testid="event-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingEvent} data-testid="save-event-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingEvent ? "Saving..." : "Save Event"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Birthday Shoutout editor drawer */}
      {editingShoutout && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeShoutout} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingShoutout === "new" ? "New Birthday Shoutout" : "Edit Shoutout"}</h3>
              <button onClick={closeShoutout} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveShoutout} className="p-6 space-y-4">
              <Field label="Name of celebrant"><input required value={shoutoutForm.name} onChange={(e) => setShoutoutForm({ ...shoutoutForm, name: e.target.value })} data-testid="shoutout-name-input" className={inputCls} /></Field>
              <Field label="Title / Tagline (optional)"><input value={shoutoutForm.title} onChange={(e) => setShoutoutForm({ ...shoutoutForm, title: e.target.value })} placeholder="e.g. Beloved Elder, Cultural Icon" data-testid="shoutout-title-input" className={inputCls} /></Field>
              <Field label="Custom date label (optional — overrides the auto date shown on the card)"><input value={shoutoutForm.date} onChange={(e) => setShoutoutForm({ ...shoutoutForm, date: e.target.value })} placeholder="e.g. June 12" data-testid="shoutout-date-input" className={inputCls} /></Field>
              <Field label="Write-up / Message"><textarea value={shoutoutForm.message} onChange={(e) => setShoutoutForm({ ...shoutoutForm, message: e.target.value })} rows={5} placeholder="Happy birthday! ÀsÀNigHT Worldwide celebrates you…" data-testid="shoutout-message-input" className={inputCls} /></Field>
              <Field label="Birthday (for auto-highlight — today's birthdays feature at the top of the Celebrate page)">
                <div className="flex gap-3">
                  <select value={shoutoutForm.birth_month} onChange={(e) => setShoutoutForm({ ...shoutoutForm, birth_month: Number(e.target.value) })} data-testid="shoutout-month-input" className={inputCls}>
                    <option value={0}>Month</option>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  <select value={shoutoutForm.birth_day} onChange={(e) => setShoutoutForm({ ...shoutoutForm, birth_day: Number(e.target.value) })} data-testid="shoutout-day-input" className={inputCls}>
                    <option value={0}>Day</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </Field>
              <Field label="Photo">
                <div className="flex flex-wrap items-center gap-3">
                  <label data-testid="shoutout-image-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                    {uploadingShoutout ? "Uploading…" : "Upload photo"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadShoutoutImage} disabled={uploadingShoutout} data-testid="shoutout-image-upload-input" className="hidden" />
                  </label>
                  <span className="text-xs text-ink-muted">or paste a URL below</span>
                </div>
                <input value={shoutoutForm.image} onChange={(e) => setShoutoutForm({ ...shoutoutForm, image: e.target.value })} placeholder="https://…" data-testid="shoutout-image-input" className={`${inputCls} mt-2`} />
              </Field>
              {shoutoutForm.image && <img src={shoutoutForm.image} alt="preview" className="w-40 h-48 object-cover rounded-lg border border-parchment" />}
              <Field label="Video (optional)">
                <div className="flex flex-wrap items-center gap-3">
                  <label data-testid="shoutout-video-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                    {uploadingShoutoutVideo ? "Uploading…" : "Upload video"}
                    <input type="file" accept="video/mp4,video/webm,video/quicktime,video/ogg" onChange={uploadShoutoutVideo} disabled={uploadingShoutoutVideo} data-testid="shoutout-video-upload-input" className="hidden" />
                  </label>
                  <span className="text-xs text-ink-muted">MP4/WEBM/MOV, up to 200MB — the photo is used as its cover</span>
                </div>
                <input value={shoutoutForm.video} onChange={(e) => setShoutoutForm({ ...shoutoutForm, video: e.target.value })} placeholder="https://… (or paste a video URL)" data-testid="shoutout-video-input" className={`${inputCls} mt-2`} />
              </Field>
              {shoutoutForm.video && (
                <div className="relative w-56">
                  <video src={shoutoutForm.video} controls playsInline className="w-full rounded-lg border border-parchment bg-night" />
                  <button type="button" onClick={() => setShoutoutForm({ ...shoutoutForm, video: "" })} data-testid="shoutout-video-clear" className="absolute -top-2 -right-2 grid place-items-center w-6 h-6 rounded-full bg-night text-cream"><X className="w-3 h-3" /></button>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={shoutoutForm.published} onChange={(e) => setShoutoutForm({ ...shoutoutForm, published: e.target.checked })} data-testid="shoutout-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingShoutout} data-testid="save-shoutout-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingShoutout ? "Saving..." : "Save Shoutout"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Gallery editor drawer */}
      {editingGallery && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeGallery} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingGallery === "new" ? "Add Photo" : "Edit Photo"}</h3>
              <button onClick={closeGallery} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveGallery} className="p-6 space-y-4">
              <Field label="Photo">
                <div className="flex flex-wrap items-center gap-3">
                  <label
                    data-testid="gallery-image-upload-label"
                    className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer"
                  >
                    {uploadingGallery ? "Uploading…" : "Upload image / video"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                      onChange={uploadGalleryImage}
                      disabled={uploadingGallery}
                      data-testid="gallery-image-upload-input"
                      className="hidden"
                    />
                  </label>
                  <span className="text-xs text-ink-muted">or paste a URL below</span>
                </div>
                <input value={galleryForm.src} onChange={(e) => setGalleryForm({ ...galleryForm, src: e.target.value })} placeholder="https://…" data-testid="gallery-src-input" className={`${inputCls} mt-2`} />
              </Field>
              <Field label="Media type">
                <select value={galleryForm.type} onChange={(e) => setGalleryForm({ ...galleryForm, type: e.target.value })} data-testid="gallery-type-input" className={inputCls}>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </Field>
              {galleryForm.src && (galleryForm.type === "video"
                ? <video src={galleryForm.src} controls poster={galleryForm.poster || undefined} className="w-full h-48 object-contain rounded-lg border border-parchment bg-night" />
                : <img src={galleryForm.src} alt="preview" className="w-full h-48 object-cover rounded-lg border border-parchment" />)}
              {galleryForm.type === "video" && (
                <Field label="Video cover / poster image (optional — shown on the tile before it plays)">
                  <div className="flex flex-wrap items-center gap-3">
                    <label data-testid="gallery-poster-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                      {uploadingGallery ? "Uploading…" : "Upload cover"}
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadGalleryPoster} disabled={uploadingGallery} data-testid="gallery-poster-upload-input" className="hidden" />
                    </label>
                    <span className="text-xs text-ink-muted">or paste a URL below</span>
                  </div>
                  <input value={galleryForm.poster} onChange={(e) => setGalleryForm({ ...galleryForm, poster: e.target.value })} placeholder="https://…" data-testid="gallery-poster-input" className={`${inputCls} mt-2`} />
                  {galleryForm.poster && <img src={galleryForm.poster} alt="cover preview" className="w-full h-32 object-cover rounded-lg border border-parchment mt-2" />}
                </Field>
              )}
              <Field label="Caption"><input value={galleryForm.caption} onChange={(e) => setGalleryForm({ ...galleryForm, caption: e.target.value })} data-testid="gallery-caption-input" className={inputCls} /></Field>
              <Field label="Category">
                <select value={galleryForm.category} onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })} data-testid="gallery-category-input" className={inputCls}>
                  {GALLERY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={galleryForm.published} onChange={(e) => setGalleryForm({ ...galleryForm, published: e.target.checked })} data-testid="gallery-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingGallery} data-testid="save-gallery-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingGallery ? "Saving..." : "Save Photo"}
              </button>
            </form>
          </div>
        </div>
      )}



      {/* Royal title editor drawer */}
      {editingTitle && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeTitle} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingTitle === "new" ? "New Royal Title" : "Edit Royal Title"}</h3>
              <button onClick={closeTitle} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveTitle} className="p-6 space-y-4">
              <Field label="Kingdom / Town"><input required value={titleForm.town} onChange={(e) => setTitleForm({ ...titleForm, town: e.target.value })} data-testid="royal-title-town-input" className={inputCls} /></Field>
              <Field label="Royal Title"><input required value={titleForm.title} onChange={(e) => setTitleForm({ ...titleForm, title: e.target.value })} data-testid="royal-title-title-input" className={inputCls} /></Field>
              <Field label="Region"><input value={titleForm.region} onChange={(e) => setTitleForm({ ...titleForm, region: e.target.value })} data-testid="royal-title-region-input" className={inputCls} /></Field>
              <Field label="Note / Significance"><textarea value={titleForm.note} onChange={(e) => setTitleForm({ ...titleForm, note: e.target.value })} rows={4} data-testid="royal-title-note-input" className={inputCls} /></Field>
              <Field label="Image (optional — replaces the default regal image on the page)">
                <div className="flex flex-wrap items-center gap-3">
                  <label data-testid="royal-title-image-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                    {uploadingTitle ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadTitleImage} disabled={uploadingTitle} data-testid="royal-title-image-upload-input" className="hidden" />
                  </label>
                  <span className="text-xs text-ink-muted">or paste a URL below</span>
                </div>
                <input value={titleForm.image} onChange={(e) => setTitleForm({ ...titleForm, image: e.target.value })} placeholder="https://…" data-testid="royal-title-image-input" className={`${inputCls} mt-2`} />
              </Field>
              {titleForm.image && <img src={titleForm.image} alt="preview" className="w-full h-44 object-cover rounded-lg border border-parchment" />}
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={titleForm.published} onChange={(e) => setTitleForm({ ...titleForm, published: e.target.checked })} data-testid="royal-title-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingTitle} data-testid="save-royal-title-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingTitle ? "Saving..." : "Save Royal Title"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Name editor drawer */}
      {editingName && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeName} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingName === "new" ? "New Name" : "Edit Name"}</h3>
              <button onClick={closeName} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveName} className="p-6 space-y-4">
              <Field label="Name (Yorùbá)"><input required value={nameForm.name} onChange={(e) => setNameForm({ ...nameForm, name: e.target.value })} data-testid="name-name-input" className={inputCls} /></Field>
              <Field label="Gender (optional)">
                <select value={nameForm.gender} onChange={(e) => setNameForm({ ...nameForm, gender: e.target.value })} data-testid="name-gender-input" className={inputCls}>
                  <option value="">—</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Unisex">Unisex</option>
                </select>
              </Field>
              <Field label="Meaning (optional)"><textarea value={nameForm.meaning} onChange={(e) => setNameForm({ ...nameForm, meaning: e.target.value })} rows={4} data-testid="name-meaning-input" className={inputCls} /></Field>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={nameForm.published} onChange={(e) => setNameForm({ ...nameForm, published: e.target.checked })} data-testid="name-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingName} data-testid="save-name-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingName ? "Saving..." : "Save Name"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Quiz editor drawer */}
      {editingQuiz && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={closeQuiz} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">{editingQuiz === "new" ? "New Question" : "Edit Question"}</h3>
              <button onClick={closeQuiz} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveQuiz} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Question Type">
                  <select value={quizForm.type} onChange={(e) => setQuizType(e.target.value)} data-testid="quiz-type-input" className={inputCls}>
                    <option value="mcq">Multiple choice</option>
                    <option value="boolean">True / False</option>
                  </select>
                </Field>
                <Field label="Category">
                  <input value={quizForm.category} onChange={(e) => setQuizForm({ ...quizForm, category: e.target.value })} data-testid="quiz-category-input" list="quiz-cats" className={inputCls} />
                  <datalist id="quiz-cats">
                    <option value="Òrìṣà" />
                    <option value="History" />
                    <option value="Language" />
                    <option value="Names & Naming" />
                    <option value="Philosophy" />
                    <option value="Proverbs" />
                    <option value="Culture" />
                  </datalist>
                </Field>
              </div>
              <Field label="Question"><textarea required value={quizForm.question} onChange={(e) => setQuizForm({ ...quizForm, question: e.target.value })} rows={3} data-testid="quiz-question-input" className={inputCls} /></Field>

              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Answer options {quizForm.type === "mcq" && <span className="text-ink-muted font-normal">(select the correct one)</span>}</label>
                {quizForm.type === "boolean" ? (
                  <div className="space-y-2">
                    {["True", "False"].map((opt, i) => (
                      <label key={opt} className="flex items-center gap-3 border border-parchment rounded-md px-4 py-2.5 bg-white cursor-pointer">
                        <input type="radio" name="quiz-correct" checked={quizForm.correct_index === i} onChange={() => setQuizForm({ ...quizForm, correct_index: i })} data-testid={`quiz-correct-${i}`} className="w-4 h-4 accent-forest" />
                        <span className="text-ink">{opt}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {quizForm.options.map((opt, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <input type="radio" name="quiz-correct" checked={quizForm.correct_index === i} onChange={() => setQuizForm({ ...quizForm, correct_index: i })} data-testid={`quiz-correct-${i}`} className="w-4 h-4 accent-forest shrink-0" title="Mark as correct" />
                        <input
                          value={(quizForm.option_labels || [])[i] ?? DEFAULT_OPTION_LETTERS[i] ?? ""}
                          onChange={(e) => { const L = [...(quizForm.option_labels || [])]; while (L.length < quizForm.options.length) L.push(DEFAULT_OPTION_LETTERS[L.length] || ""); L[i] = e.target.value; setQuizForm({ ...quizForm, option_labels: L }); }}
                          data-testid={`quiz-option-label-${i}`}
                          title="Option label (editable)"
                          className="w-12 text-center border border-parchment rounded-md px-1 py-2.5 text-forest font-semibold bg-white focus:outline-none focus:border-forest shrink-0"
                        />
                        <input value={opt} onChange={(e) => { const o = [...quizForm.options]; o[i] = e.target.value; setQuizForm({ ...quizForm, options: o }); }} placeholder={`Option ${DEFAULT_OPTION_LETTERS[i]}${i < 2 ? " (required)" : " (optional)"}`} data-testid={`quiz-option-input-${i}`} className={inputCls} />
                      </div>
                    ))}
                    <p className="text-xs text-ink-muted">The small box is the option label (default A–D) — edit it to any label you like. Fill at least two options and use the radio to mark the correct answer.</p>
                  </div>
                )}
              </div>

              <Field label="Explanation (shown after answering, optional)"><textarea value={quizForm.explanation} onChange={(e) => setQuizForm({ ...quizForm, explanation: e.target.value })} rows={3} data-testid="quiz-explanation-input" className={inputCls} /></Field>
              <Field label="Image (optional)">
                <div className="flex flex-wrap items-center gap-3">
                  <label data-testid="quiz-image-upload-label" className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
                    {uploadingQuiz ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadQuizImage} disabled={uploadingQuiz} data-testid="quiz-image-upload-input" className="hidden" />
                  </label>
                  <span className="text-xs text-ink-muted">or paste a URL below</span>
                </div>
                <input value={quizForm.image} onChange={(e) => setQuizForm({ ...quizForm, image: e.target.value })} placeholder="https://…" data-testid="quiz-image-input" className={`${inputCls} mt-2`} />
              </Field>
              {quizForm.image && <img src={quizForm.image} alt="preview" className="w-full h-40 object-cover rounded-lg border border-parchment" />}
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={quizForm.published} onChange={(e) => setQuizForm({ ...quizForm, published: e.target.checked })} data-testid="quiz-published-checkbox" className="w-4 h-4 accent-forest" />
                Published
              </label>
              <button type="submit" disabled={savingQuiz} data-testid="save-quiz-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingQuiz ? "Saving..." : "Save Question"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Bulk add modal */}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-night/60" onClick={() => setBulkOpen(false)} />
          <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-serif text-2xl text-ink">Bulk Add Questions</h3>
              <button onClick={() => setBulkOpen(false)} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-sand border border-parchment rounded-lg p-4 text-sm text-ink-muted space-y-2">
                <p className="text-ink font-medium">One question per line, separated by <code className="bg-white px-1 rounded">|</code></p>
                <p><strong>Multiple choice:</strong><br /><code className="text-xs">Category | Question | Correct answer | Wrong | Wrong | Wrong</code></p>
                <p>The correct answer goes first; options are shuffled automatically.</p>
                <p><strong>True / False:</strong><br /><code className="text-xs">Category | Question | True | False</code> (put the correct one first)</p>
              </div>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={12}
                placeholder={"Òrìṣà | Who is the Òrìṣà of iron? | Ògún | Ṣàngó | Ọ̀ṣun | Obàtálá\nLanguage | Yorùbá is a tonal language. | True | False"}
                data-testid="bulk-quiz-textarea"
                className={inputCls + " font-mono text-sm"}
              />
              <button onClick={saveBulk} disabled={savingBulk} data-testid="save-bulk-quiz-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
                {savingBulk ? "Adding..." : "Add Questions"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* People / Donations viewer drawer */}
      {viewer && (() => {
        const cfg = VIEWER_CONFIG[viewer];
        const rows = { subscribers, members, volunteers, donations }[viewer] || [];
        return (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-night/60" onClick={() => setViewer(null)} />
            <div className="relative w-full max-w-3xl bg-cream h-full overflow-y-auto shadow-2xl" data-testid="viewer-drawer">
              <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h3 className="font-serif text-2xl text-ink">{cfg.title}</h3>
                  <p className="text-sm text-ink-muted">{rows.length} {rows.length === 1 ? "record" : "records"}</p>
                </div>
                <div className="flex items-center gap-2">
                  {rows.length > 0 && (
                    <button
                      onClick={() => downloadCsv(`${viewer}.csv`, cfg.columns, rows)}
                      data-testid="viewer-export-csv"
                      className="inline-flex items-center gap-2 border border-forest text-forest text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest hover:text-cream transition-colors"
                    >
                      <Download className="w-4 h-4" /> Export CSV
                    </button>
                  )}
                  <button onClick={() => setViewer(null)} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <div className="p-6">
                {cfg.note && (
                  <div className="mb-4 rounded-lg bg-gold/10 border border-gold/30 text-ink text-sm px-4 py-3">{cfg.note}</div>
                )}
                {rows.length === 0 ? (
                  <p className="text-ink-muted py-12 text-center" data-testid="viewer-empty">{cfg.empty}</p>
                ) : (
                  <div className="overflow-x-auto border border-parchment rounded-lg">
                    <table className="w-full text-sm" data-testid="viewer-table">
                      <thead>
                        <tr className="bg-sand text-left text-ink-muted">
                          {cfg.columns.map((c) => (
                            <th key={c.key} className="px-4 py-3 font-medium whitespace-nowrap">{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={r.id || i} className="border-t border-parchment align-top">
                            {cfg.columns.map((c) => (
                              <td key={c.key} className="px-4 py-3 text-ink max-w-[240px] break-words">{fmtCell(r[c.key], c)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {uploadPct !== null && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] w-[min(90vw,420px)] bg-night text-cream rounded-xl shadow-2xl px-5 py-4" data-testid="upload-progress">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="inline-flex items-center gap-2 font-semibold"><Upload className="w-4 h-4 text-gold" /> Uploading…</span>
            <span className="text-gold font-bold" data-testid="upload-progress-pct">{uploadPct}%</span>
          </div>
          <div className="h-2 rounded-full bg-cream/15 overflow-hidden">
            <div className="h-full bg-gold transition-all duration-200" style={{ width: `${uploadPct}%` }} />
          </div>
          <p className="text-xs text-cream/60 mt-2">Large videos may take a moment — please keep this tab open.</p>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full border border-parchment rounded-md px-4 py-2.5 outline-none focus:border-gold focus:ring-1 focus:ring-gold bg-white";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink mb-1.5">{label}</label>
      {children}
    </div>
  );
}
